import { useState, useEffect, useRef, createContext, useContext, useMemo, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import "./App.css";
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';
import './editor-custom.css';
import 'flag-icons/css/flag-icons.min.css';
import { AgentPortal } from './components/AgentPortal';
import { TasksKanban } from './components/TasksKanban';
import { MediaCenter } from './components/MediaCenter';
import { CRM } from './components/CRM';
import { AirportContactFinder } from './components/AirportContactFinder';
import { OfferCenter } from './components/OfferCenter';

// Register image resize module
Quill.register('modules/imageResize', ImageResize);

// Components
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Checkbox } from "./components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  console.error('REACT_APP_BACKEND_URL environment variable is not set!');
}
const API = `${BACKEND_URL}/api`;

// Helper function to get the correct image URL
const getImageUrl = (url) => {
  if (!url) return null;
  // If it's already an absolute URL (https://), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If it starts with /api, prepend BACKEND_URL
  if (url.startsWith('/api')) {
    return `${BACKEND_URL}${url}`;
  }
  // Otherwise prepend API
  return `${API}${url}`;
};

// Helper function to check if user is admin
const isAdmin = (user) => {
  if (!user) return false;
  // Check by role type (primary), role name, or specific admin email
  return user.role?.type === 'admin' || 
         user.role?.name === 'Administrator' || 
         user.email === 'admin@velocityairportsolutions.com';
};

// Helper function to get user initials
const getUserInitials = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Currency List - Top 10 most used currencies first, then alphabetical
const CURRENCIES = [
  // Top 10 most used currencies
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'DKK', name: 'Danish Krone' },
  // Rest alphabetically
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'AFN', name: 'Afghan Afghani' },
  { code: 'ALL', name: 'Albanian Lek' },
  { code: 'AMD', name: 'Armenian Dram' },
  { code: 'ANG', name: 'Netherlands Antillean Guilder' },
  { code: 'AOA', name: 'Angolan Kwanza' },
  { code: 'ARS', name: 'Argentine Peso' },
  { code: 'AWG', name: 'Aruban Florin' },
  { code: 'AZN', name: 'Azerbaijani Manat' },
  { code: 'BAM', name: 'Bosnia-Herzegovina Convertible Mark' },
  { code: 'BBD', name: 'Barbadian Dollar' },
  { code: 'BDT', name: 'Bangladeshi Taka' },
  { code: 'BGN', name: 'Bulgarian Lev' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'BIF', name: 'Burundian Franc' },
  { code: 'BMD', name: 'Bermudan Dollar' },
  { code: 'BND', name: 'Brunei Dollar' },
  { code: 'BOB', name: 'Bolivian Boliviano' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'BSD', name: 'Bahamian Dollar' },
  { code: 'BTN', name: 'Bhutanese Ngultrum' },
  { code: 'BWP', name: 'Botswanan Pula' },
  { code: 'BYN', name: 'Belarusian Ruble' },
  { code: 'BZD', name: 'Belize Dollar' },
  { code: 'CDF', name: 'Congolese Franc' },
  { code: 'CLP', name: 'Chilean Peso' },
  { code: 'COP', name: 'Colombian Peso' },
  { code: 'CRC', name: 'Costa Rican Colón' },
  { code: 'CUP', name: 'Cuban Peso' },
  { code: 'CVE', name: 'Cape Verdean Escudo' },
  { code: 'CZK', name: 'Czech Republic Koruna' },
  { code: 'DJF', name: 'Djiboutian Franc' },
  { code: 'DOP', name: 'Dominican Peso' },
  { code: 'DZD', name: 'Algerian Dinar' },
  { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'ERN', name: 'Eritrean Nakfa' },
  { code: 'ETB', name: 'Ethiopian Birr' },
  { code: 'FJD', name: 'Fijian Dollar' },
  { code: 'FKP', name: 'Falkland Islands Pound' },
  { code: 'GEL', name: 'Georgian Lari' },
  { code: 'GGP', name: 'Guernsey Pound' },
  { code: 'GHS', name: 'Ghanaian Cedi' },
  { code: 'GIP', name: 'Gibraltar Pound' },
  { code: 'GMD', name: 'Gambian Dalasi' },
  { code: 'GNF', name: 'Guinean Franc' },
  { code: 'GTQ', name: 'Guatemalan Quetzal' },
  { code: 'GYD', name: 'Guyanaese Dollar' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'HNL', name: 'Honduran Lempira' },
  { code: 'HRK', name: 'Croatian Kuna' },
  { code: 'HTG', name: 'Haitian Gourde' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'ILS', name: 'Israeli New Sheqel' },
  { code: 'IMP', name: 'Manx pound' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'IQD', name: 'Iraqi Dinar' },
  { code: 'IRR', name: 'Iranian Rial' },
  { code: 'ISK', name: 'Icelandic Króna' },
  { code: 'JEP', name: 'Jersey Pound' },
  { code: 'JMD', name: 'Jamaican Dollar' },
  { code: 'JOD', name: 'Jordanian Dinar' },
  { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'KGS', name: 'Kyrgystani Som' },
  { code: 'KHR', name: 'Cambodian Riel' },
  { code: 'KMF', name: 'Comorian Franc' },
  { code: 'KPW', name: 'North Korean Won' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'KYD', name: 'Cayman Islands Dollar' },
  { code: 'KZT', name: 'Kazakhstani Tenge' },
  { code: 'LAK', name: 'Laotian Kip' },
  { code: 'LBP', name: 'Lebanese Pound' },
  { code: 'LKR', name: 'Sri Lankan Rupee' },
  { code: 'LRD', name: 'Liberian Dollar' },
  { code: 'LSL', name: 'Lesotho Loti' },
  { code: 'LYD', name: 'Libyan Dinar' },
  { code: 'MAD', name: 'Moroccan Dirham' },
  { code: 'MDL', name: 'Moldovan Leu' },
  { code: 'MGA', name: 'Malagasy Ariary' },
  { code: 'MKD', name: 'Macedonian Denar' },
  { code: 'MMK', name: 'Myanma Kyat' },
  { code: 'MNT', name: 'Mongolian Tugrik' },
  { code: 'MOP', name: 'Macanese Pataca' },
  { code: 'MRU', name: 'Mauritanian Ouguiya' },
  { code: 'MUR', name: 'Mauritian Rupee' },
  { code: 'MVR', name: 'Maldivian Rufiyaa' },
  { code: 'MWK', name: 'Malawian Kwacha' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'MZN', name: 'Mozambican Metical' },
  { code: 'NAD', name: 'Namibian Dollar' },
  { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'NIO', name: 'Nicaraguan Córdoba' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'NPR', name: 'Nepalese Rupee' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'PAB', name: 'Panamanian Balboa' },
  { code: 'PEN', name: 'Peruvian Nuevo Sol' },
  { code: 'PGK', name: 'Papua New Guinean Kina' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'PKR', name: 'Pakistani Rupee' },
  { code: 'PLN', name: 'Polish Zloty' },
  { code: 'PYG', name: 'Paraguayan Guarani' },
  { code: 'QAR', name: 'Qatari Rial' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'RSD', name: 'Serbian Dinar' },
  { code: 'RUB', name: 'Russian Ruble' },
  { code: 'RWF', name: 'Rwandan Franc' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'SBD', name: 'Solomon Islands Dollar' },
  { code: 'SCR', name: 'Seychellois Rupee' },
  { code: 'SDG', name: 'Sudanese Pound' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'SHP', name: 'Saint Helena Pound' },
  { code: 'SLE', name: 'Sierra Leonean Leone' },
  { code: 'SOS', name: 'Somali Shilling' },
  { code: 'SRD', name: 'Surinamese Dollar' },
  { code: 'STN', name: 'São Tomé and Príncipe Dobra' },
  { code: 'SVC', name: 'Salvadoran Colón' },
  { code: 'SYP', name: 'Syrian Pound' },
  { code: 'SZL', name: 'Swazi Lilangeni' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'TJS', name: 'Tajikistani Somoni' },
  { code: 'TMT', name: 'Turkmenistani Manat' },
  { code: 'TND', name: 'Tunisian Dinar' },
  { code: 'TOP', name: 'Tongan Pa\'anga' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'TTD', name: 'Trinidad and Tobago Dollar' },
  { code: 'TVD', name: 'Tuvaluan Dollar' },
  { code: 'TWD', name: 'New Taiwan Dollar' },
  { code: 'TZS', name: 'Tanzanian Shilling' },
  { code: 'UAH', name: 'Ukrainian Hryvnia' },
  { code: 'UGX', name: 'Ugandan Shilling' },
  { code: 'UYU', name: 'Uruguayan Peso' },
  { code: 'UZS', name: 'Uzbekistan Som' },
  { code: 'VED', name: 'Venezuelan Bolívar' },
  { code: 'VES', name: 'Venezuelan Bolívar Soberano' },
  { code: 'VND', name: 'Vietnamese Dong' },
  { code: 'VUV', name: 'Vanuatu Vatu' },
  { code: 'WST', name: 'Samoan Tala' },
  { code: 'XAF', name: 'CFA Franc BEAC' },
  { code: 'XCD', name: 'East Caribbean Dollar' },
  { code: 'XDR', name: 'Special Drawing Rights' },
  { code: 'XOF', name: 'CFA Franc BCEAO' },
  { code: 'XPF', name: 'CFP Franc' },
  { code: 'YER', name: 'Yemeni Rial' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'ZMW', name: 'Zambian Kwacha' },
  { code: 'ZWL', name: 'Zimbabwean Dollar' }
];

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      console.log('Fetching current user from:', `${API}/auth/me`);
      const response = await axios.get(`${API}/auth/me`, {
        timeout: 10000 // 10 second timeout
      });
      console.log('User data received:', response.data);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('Attempting login...');
      const response = await axios.post(`${API}/auth/login`, { email, password }, {
        timeout: 10000 // 10 second timeout
      });
      const { access_token } = response.data;
      
      console.log('Login successful, storing token...');
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      console.log('Fetching current user...');
      await fetchCurrentUser();
      console.log('Login complete');
      return true;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Login failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Welcome to Velocity Airport Solutions!');
      navigate('/'); // Redirect to dashboard after successful login
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_leadscan-1/artifacts/gdd6nb2q_logo.png" 
              alt="Velocity" 
              className="h-16 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">Welcome Back</CardTitle>
          <CardDescription>Sign in to your Velocity account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-sky-600 hover:bg-sky-700" 
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Sidebar Navigation Component
const Sidebar = ({ activeTab, setActiveTab }) => {
  const { user, logout } = useAuth();

  const navigationItems = [
    { value: 'dashboard', label: 'Dashboard', icon: '📊', adminOnly: false },
    { value: 'users', label: 'Users', icon: '👥', adminOnly: true },
    { value: 'ges', label: 'GEs', icon: '🌍', adminOnly: false },
    { value: 'cards', label: 'Cards', icon: '📇', adminOnly: false },
    { value: 'receipts', label: 'Receipts', icon: '🧾', adminOnly: false },
    { value: 'mileage', label: 'Mileage', icon: '🚗', adminOnly: false },
    { value: 'reports', label: 'Reports', icon: '📈', adminOnly: false },
    { value: 'marketing', label: 'Marketing', icon: '📧', adminOnly: true },
    { value: 'agents', label: 'Agent Portal', icon: '🎯', adminOnly: true },
    { value: 'tasks', label: 'To-Do', icon: '✅', adminOnly: false },
    { value: 'crm', label: 'CRM', icon: '💼', adminOnly: false },
    { value: 'offers', label: 'Offer Center', icon: '📝', adminOnly: false },
    { value: 'media', label: 'Media Center', icon: '📁', adminOnly: false },
    { value: 'experts', label: 'Experts', icon: '🤖', adminOnly: false },
    { value: 'settings', label: user && isAdmin(user) ? 'Settings' : 'My Profile', icon: '⚙️', adminOnly: false },
  ];

  const visibleItems = navigationItems.filter(item => 
    !item.adminOnly || (user && isAdmin(user))
  );

  return (
    <div className="fixed left-0 top-0 h-screen w-64 bg-gradient-to-b from-indigo-600 to-indigo-800 text-white flex flex-col shadow-lg">
      {/* Logo Section */}
      <div className="p-6 border-b border-indigo-500">
        <div 
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => setActiveTab('dashboard')}
          data-testid="logo-home-button"
        >
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_leadscan-1/artifacts/gdd6nb2q_logo.png" 
              alt="Velocity" 
              className="w-8 h-8 object-contain"
            />
          </div>
          <div>
            <h1 className="text-lg font-bold">Velocity Intranet</h1>
            <p className="text-xs text-indigo-200">System</p>
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <div className="space-y-1">
          {visibleItems.map((item) => (
            <button
              key={item.value}
              onClick={() => setActiveTab(item.value)}
              data-testid={`${item.value}-tab`}
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all
                ${activeTab === item.value 
                  ? 'bg-white text-indigo-700 font-semibold shadow-md' 
                  : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
                }
              `}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="text-sm">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* User Profile Section */}
      <div className="border-t border-indigo-500 p-4">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="h-10 w-10 border-2 border-white">
            {user?.photo_url && (
              <AvatarImage 
                src={`${BACKEND_URL}${user.photo_url}`} 
                alt={user?.name}
              />
            )}
            <AvatarFallback className="bg-indigo-900 text-white">
              {user?.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-indigo-200 truncate">{user?.role?.name || 'User'}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={logout}
          data-testid="logout-button"
          className="w-full justify-start text-indigo-100 hover:bg-indigo-700 hover:text-white"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Logout
        </Button>
      </div>
    </div>
  );
};

// Business Card Scanner Component
const BusinessCardScanner = () => {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailLists, setEmailLists] = useState([]);
  
  // Filter states
  const [filterEvent, setFilterEvent] = useState('all');
  const [filterOwner, setFilterOwner] = useState('all');
  
  // View states  
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'table', or 'events'
  
  // Email dialog states
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  // Image modal states
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedCardImage, setSelectedCardImage] = useState(null);
  // Manual entry dialog states
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  const [manualEntryData, setManualEntryData] = useState({
    name: '',
    company: '',
    position: '',
    email: '',
    phone: '',
    mobile: '',
    website: '',
    address: '',
    notes: ''
  });
  const [thankYouEmailData, setThankYouEmailData] = useState({
    recipient_email: '',
    recipient_name: '',
    company: '',
    event_name: '',
    interests: [],
    subject: '',
    body: ''
  });
  
  // Scanning Modal States
  const [showScanModal, setShowScanModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState('no-event');
  const [selectedEventDetails, setSelectedEventDetails] = useState(null);
  const [eventInterests, setEventInterests] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [otherInterest, setOtherInterest] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    position: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    country: '',
    website: '',
    notes: ''
  });

  // Edit Modal States
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [editCard, setEditCard] = useState({
    company: '',
    name: '',
    position: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    country: '',
    website: '',
    notes: ''
  });
  const [activeReport, setActiveReport] = useState(null); // Track which report is currently showing

  useEffect(() => {
    fetchData();
    if (user) {
      fetchEmailLists();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [cardsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/cards`),
        axios.get(`${API}/events`)
      ]);
      setCards(cardsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };


  const fetchEmailLists = async () => {
    if (!user || !isAdmin(user)) return;
    try {
      const response = await axios.get(`${API}/email-lists`);
      console.log('Fetched email lists:', response.data);
      setEmailLists(response.data);
    } catch (error) {
      console.error('Failed to fetch email lists:', error);
    }
  };

  const addSubscribers = async (listId, file, singleEmail) => {
    try {
      const formData = new FormData();
      if (file) {
        formData.append('subscribers_csv', file);
      } else if (singleEmail) {
        formData.append('email', singleEmail.email);
        formData.append('first_name', singleEmail.first_name || '');
        formData.append('last_name', singleEmail.last_name || '');
      }

      const response = await axios.post(`${API}/email-lists/${listId}/subscribers`, formData);
      toast.success(`Added ${response.data.added} subscriber(s)`);
      fetchEmailLists();
    } catch (error) {
      toast.error('Failed to add subscribers');
    }
  };

  const openScanModal = () => {
    setShowScanModal(true);
    setCapturedImage(null);
    setCroppedImageUrl(null);
    setExtractedData(null);
    
    // Remember last selected event from localStorage
    const lastSelectedEvent = localStorage.getItem('lastSelectedEvent');
    if (lastSelectedEvent && events.some(e => e.id === lastSelectedEvent)) {
      setSelectedEvent(lastSelectedEvent);
      // Load the event details and interests for the remembered event
      const eventDetails = events.find(e => e.id === lastSelectedEvent);
      if (eventDetails) {
        setSelectedEventDetails(eventDetails);
        setEventInterests(eventDetails.areas_of_interest || []);
      }
    } else {
      setSelectedEvent('no-event');
      setSelectedEventDetails(null);
      setEventInterests([]);
    }
    
    setSelectedInterests([]);
    setOtherInterest('');
    setFormData({
      company: '',
      name: '',
      position: '',
      email: '',
      phone: '',
      mobile: '',
      address: '',
      country: '',
      website: '',
      notes: ''
    });
  };

  const handleEventSelection = async (eventId) => {
    setSelectedEvent(eventId);
    
    // Save the selected event to localStorage for next time
    if (eventId !== 'no-event') {
      localStorage.setItem('lastSelectedEvent', eventId);
    }
    
    if (eventId !== 'no-event') {
      // Find the selected event details
      const event = events.find(e => e.id === eventId);
      setSelectedEventDetails(event);
      
      // Fetch interests for this event
      try {
        const response = await axios.get(`${API}/events/${eventId}/interests`);
        setEventInterests(response.data);
      } catch (error) {
        console.error('Failed to fetch event interests:', error);
        setEventInterests([]);
      }
    } else {
      setSelectedEventDetails(null);
      setEventInterests([]);
    }
    
    // Reset selections
    setSelectedInterests([]);
    setOtherInterest('');
  };

  const openImageModal = (card) => {
    setSelectedCardImage(card);
    setShowImageModal(true);
  };

  const saveManualContact = async () => {
    try {
      // Prepare interests data (convert IDs to labels)
      const interestLabels = selectedInterests.map(id => {
        const interest = eventInterests.find(i => i.id === id);
        return interest ? interest.label : null;
      }).filter(Boolean);
      
      if (otherInterest && otherInterest.trim()) {
        interestLabels.push(otherInterest.trim());
      }
      
      // Prepare card data
      const cardData = {
        ...manualEntryData,
        event_id: selectedEvent !== 'no-event' ? selectedEvent : null,
        interests: interestLabels,
        other_interest: otherInterest || null,
        manual_entry: true,
        // No image URLs for manual entry
        image_url: null,
        cropped_image_url: null
      };
      
      await axios.post(`${API}/cards`, cardData);
      
      // Add new interest to event if provided
      if (otherInterest.trim() && selectedEvent !== 'no-event') {
        try {
          await axios.post(`${API}/events/${selectedEvent}/interests`, {
            label: otherInterest.trim()
          });
          console.log(`Added new interest "${otherInterest.trim()}" to event ${selectedEvent}`);
          
          // Refresh the event interests to include the new one
          try {
            const response = await axios.get(`${API}/events/${selectedEvent}/interests`);
            setEventInterests(response.data);
          } catch (error) {
            console.error('Failed to refresh event interests:', error);
          }
          toast.success(`Added "${otherInterest.trim()}" to event interests!`);
        } catch (error) {
          console.error('Failed to add new interest to event:', error);
          toast.error(`Failed to add "${otherInterest.trim()}" to event interests`);
        }
      }
      
      toast.success('Contact added successfully!');
      
      // Reset all states
      setShowManualEntryDialog(false);
      setManualEntryData({
        name: '',
        company: '',
        position: '',
        email: '',
        phone: '',
        mobile: '',
        website: '',
        address: '',
        notes: ''
      });
      setSelectedEvent('no-event');
      setSelectedInterests([]);
      setEventInterests([]);
      setOtherInterest('');
      
      // Refresh cards list
      fetchData();
      
    } catch (error) {
      console.error('Error saving manual contact:', error);
      toast.error('Failed to save contact');
    }
  };

  const handleImageCapture = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Show image preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setCapturedImage(e.target.result);
    };
    reader.readAsDataURL(file);

    // Process with AI
    setIsProcessing(true);
    toast.info('Processing business card... This may take 15-30 seconds');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedEvent && selectedEvent !== 'no-event') {
        formData.append('event_id', selectedEvent);
      }

      const response = await axios.post(`${API}/cards/scan`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 second timeout
      });

      const extracted = response.data.extracted_data;
      setExtractedData(extracted);
      
      // Store the cropped image URL for saving with the card
      setCroppedImageUrl(response.data.cropped_image_url);
      
      // Ensure event interests are preserved after scanning
      if (selectedEvent && selectedEvent !== 'no-event' && eventInterests.length === 0) {
        console.log('Refreshing event interests after scan...');
        try {
          const interestsResponse = await axios.get(`${API}/events/${selectedEvent}/interests`);
          setEventInterests(interestsResponse.data);
        } catch (error) {
          console.error('Failed to refresh event interests:', error);
        }
      }
      
      // Show the cropped business card image if available
      if (response.data.cropped_image_url) {
        setCapturedImage(getImageUrl(response.data.cropped_image_url));
      }
      
      // Pre-fill form with extracted data
      setFormData({
        company: extracted.company || '',
        name: extracted.name || '',
        position: extracted.position || '',
        email: extracted.email || '',
        phone: extracted.phone || '',
        mobile: extracted.mobile || '',
        address: extracted.address || '',
        country: extracted.country || '',
        website: extracted.website || '',
        notes: ''
      });

    } catch (error) {
      console.error('Card scanning error:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Processing timed out. Please try again or use "Add Contact Manually"');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to process card. Try "Add Contact Manually"');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const saveCard = async () => {
    try {
      // Prepare interests data
      const interestLabels = selectedInterests.map(id => {
        const interest = eventInterests.find(i => i.id === id);
        return interest ? interest.label : null;
      }).filter(Boolean);
      
      if (otherInterest.trim()) {
        interestLabels.push(otherInterest.trim());
      }
      
      // Create the card using the form data
      const cardData = {
        event_id: selectedEvent !== 'no-event' ? selectedEvent : '',
        image_url: croppedImageUrl,
        interests: interestLabels,
        other_interest: otherInterest.trim() || null,
        ...formData
      };

      await axios.post(`${API}/cards`, cardData);
      
      // Add new interest to event if provided
      if (otherInterest.trim() && selectedEvent !== 'no-event') {
        try {
          await axios.post(`${API}/events/${selectedEvent}/interests`, {
            label: otherInterest.trim()
          });
          console.log(`Added new interest "${otherInterest.trim()}" to event ${selectedEvent}`);
          
          // Refresh the event interests to include the new one
          try {
            const response = await axios.get(`${API}/events/${selectedEvent}/interests`);
            setEventInterests(response.data);
          } catch (error) {
            console.error('Failed to refresh event interests:', error);
          }
          toast.success(`Added "${otherInterest.trim()}" to event interests!`);
        } catch (error) {
          console.error('Failed to add new interest to event:', error);
          console.error('Error details:', error.response?.data);
          toast.error(`Failed to add "${otherInterest.trim()}" to event interests`);
        }
      }
      
      toast.success('Contact saved successfully!');
      
      // Reset all modal states
      setShowScanModal(false);
      setCapturedImage(null);
      setCroppedImageUrl(null);
      setExtractedData(null);
      setFormData({
        company: '',
        name: '',
        position: '',
        email: '',
        phone: '',
        mobile: '',
        address: '',
        country: '',
        website: '',
        notes: ''
      });
      setSelectedEvent('no-event');
      setSelectedEventDetails(null);
      setEventInterests([]);
      setSelectedInterests([]);
      setOtherInterest('');
      
      fetchData();
    } catch (error) {
      toast.error('Failed to save contact');
    }
  };

  const openEditCard = async (card) => {
    setEditingCard(card);
    setEditCard({
      company: card.company || '',
      name: card.name || '',
      position: card.position || '',
      email: card.email || '',
      phone: card.phone || '',
      mobile: card.mobile || '',
      address: card.address || '',
      country: card.country || '',
      website: card.website || '',
      notes: card.notes || ''
    });
    
    // Set the event and load its interests for editing
    if (card.event_id) {
      setSelectedEvent(card.event_id);
      setSelectedEventDetails(events.find(e => e.id === card.event_id) || null);
      
      // Load event interests
      try {
        const response = await axios.get(`${API}/events/${card.event_id}/interests`);
        setEventInterests(response.data);
        
        // Convert card's interest labels to IDs for checkboxes
        const cardInterestLabels = card.interests || [];
        const matchedIds = response.data
          .filter(interest => cardInterestLabels.includes(interest.label))
          .map(interest => interest.id);
        
        setSelectedInterests(matchedIds);
        
        // Set other interest if it exists and isn't in the predefined list
        const predefinedLabels = response.data.map(i => i.label);
        const otherInterests = cardInterestLabels.filter(label => !predefinedLabels.includes(label));
        if (otherInterests.length > 0) {
          setOtherInterest(otherInterests.join(', '));
        } else {
          setOtherInterest('');
        }
      } catch (error) {
        console.error('Failed to load event interests for editing:', error);
        setEventInterests([]);
        setSelectedInterests([]);
      }
    } else {
      setSelectedEvent('no-event');
      setSelectedEventDetails(null);
      setEventInterests([]);
      setSelectedInterests([]);
    }
    
    setShowEditDialog(true);
  };

  const enhanceCard = async (cardId) => {
    try {
      toast.info('Enhancing contact information...');
      const response = await axios.post(`${API}/cards/${cardId}/enhance`);
      toast.success('Contact information enhanced successfully');
      fetchData(); // Refresh the cards list to show enhanced data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enhance contact information');
    }
  };

  const updateCard = async () => {
    try {
      // Prepare interests data (convert IDs to labels like in saveCard)
      const interestLabels = selectedInterests.map(id => {
        const interest = eventInterests.find(i => i.id === id);
        return interest ? interest.label : null;
      }).filter(Boolean);
      
      if (otherInterest && otherInterest.trim()) {
        interestLabels.push(otherInterest.trim());
      }
      
      // Check if there's a new image to upload
      if (editCard.newImage) {
        // Upload new image without re-scanning
        const formData = new FormData();
        formData.append('file', editCard.newImage);
        formData.append('card_id', editingCard.id);
        
        try {
          await axios.post(`${API}/cards/${editingCard.id}/update-image`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          toast.success('Card image updated successfully');
        } catch (imageError) {
          toast.error('Failed to update image, but will continue updating contact info');
          console.error('Image update error:', imageError);
        }
      }
      
      // Prepare updated card data including interests
      const cardData = {
        ...editCard,
        event_id: selectedEvent !== 'no-event' ? selectedEvent : null,
        interests: interestLabels,
        other_interest: otherInterest || null
      };
      
      // Remove newImage from cardData as it's not part of the card model
      delete cardData.newImage;
      
      await axios.put(`${API}/cards/${editingCard.id}`, cardData);
      toast.success('Contact updated successfully');
      setShowEditDialog(false);
      setEditingCard(null);
      
      // Reset form state
      setSelectedEvent('no-event');
      setSelectedEventDetails(null);
      setEventInterests([]);
      setSelectedInterests([]);
      setOtherInterest('');
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    }
  };

  const openThankYouEmailDialog = async (card) => {
    // Pre-populate email data
    const eventName = events.find(e => e.id === card.event_id)?.name || 'our event';
    const recipientName = card.name || 'valued contact';
    const company = card.company || 'your organization';
    
    // Convert interest IDs to interest names for better email generation
    let interests = [];
    if (card.interests && card.interests.length > 0) {
      // Get event details to access interests
      const cardEvent = events.find(e => e.id === card.event_id);
      if (cardEvent && cardEvent.areas_of_interest) {
        // Map card interests (which might be IDs or labels) to actual labels
        interests = card.interests.map(interest => {
          // Check if it's already a label/name (string without UUID format)
          if (typeof interest === 'string' && !interest.includes('-')) {
            return interest;
          }
          // Otherwise, find the interest by ID
          const interestObj = cardEvent.areas_of_interest.find(ai => ai.id === interest);
          return interestObj ? interestObj.label : interest;
        }).filter(Boolean);
      } else {
        // Fallback: use interests as-is if no event data available
        interests = card.interests.filter(Boolean);
      }
    }
    
    setThankYouEmailData({
      recipient_email: card.email || '',
      recipient_name: recipientName,
      company: company,
      event_name: eventName,
      interests: interests,
      subject: `Thank you for visiting ${eventName} - Velocity Airport Solutions`,
      body: 'Generating personalized email...'
    });
    
    setShowEmailDialog(true);
    
    // Generate AI email content
    try {
      const response = await axios.post(`${API}/generate-thank-you-email`, {
        recipient_name: recipientName,
        company: company,
        event_name: eventName,
        interests: interests
      });
      
      setThankYouEmailData(prev => ({
        ...prev,
        subject: response.data.subject,
        body: response.data.body
      }));
    } catch (error) {
      toast.error('Failed to generate email content');
      setThankYouEmailData(prev => ({
        ...prev,
        body: `Dear ${recipientName},

Thank you for visiting our booth at ${eventName}. It was a pleasure meeting you and discussing your needs.

We believe our airport solutions can significantly benefit ${company}, especially in the areas you mentioned${interests.length > 0 ? ': ' + interests.join(', ') : ''}.

We would welcome the opportunity to continue our conversation and explore how we can support your organization.

Best regards,
Velocity Airport Solutions Team`
      }));
    }
  };

  const deleteCard = async (cardId, contactName) => {
    if (window.confirm(`Are you sure you want to delete "${contactName}"?`)) {
      try {
        await axios.delete(`${API}/cards/${cardId}`);
        toast.success('Contact deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete contact');
      }
    }
  };

  const updateEmailStatus = async (cardId, field, value) => {
    try {
      const payload = {};
      payload[field] = value;
      
      await axios.patch(`${API}/cards/${cardId}/email-status`, payload);
      
      // Update local state
      setCards(prevCards => 
        prevCards.map(card => 
          card.id === cardId ? { ...card, [field]: value } : card
        )
      );
      
      toast.success(field === 'email_sent' ? 
        (value ? 'Marked as email sent' : 'Unmarked email sent') :
        (value ? 'Marked as responded' : 'Unmarked response')
      );
    } catch (error) {
      console.error('Failed to update email status:', error);
      toast.error('Failed to update email status');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-900">Business Card Scanner</h2>
          <p className="text-sm md:text-base text-slate-600">Scan business cards and name tags with AI-powered OCR</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowManualEntryDialog(true)}
            variant="outline"
            className="w-full sm:w-auto"
            data-testid="add-manual-button"
          >
            <span className="mr-2">✏️</span>
            Add Contact Manually
          </Button>
          <Button 
            onClick={openScanModal}
            className="bg-sky-600 hover:bg-sky-700 w-full sm:w-auto"
            data-testid="scan-card-button"
          >
            <span className="mr-2">📱</span>
            Scan New Card
          </Button>
        </div>
      </div>

      {/* Filter Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="filter-event-cards">Filter by Event:</Label>
              <Select value={filterEvent} onValueChange={setFilterEvent}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Owner filter - only visible to admins */}
            {user && isAdmin(user) && (
              <div className="flex items-center gap-2">
                <Label htmlFor="filter-owner">Filter by Owner:</Label>
                <Select value={filterOwner} onValueChange={setFilterOwner}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {Array.from(new Set(cards.map(c => c.created_by_name).filter(Boolean))).sort().map(owner => (
                      <SelectItem key={owner} value={owner}>
                        {owner}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 mt-4">
            <Label>View:</Label>
            <div className="flex bg-gray-100 rounded-md p-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="px-3"
              >
                Grid
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className="px-3"
              >
                Table
              </Button>
              {/* Events view - Admin only */}
              {user && isAdmin(user) && (
                <Button
                  variant={viewMode === 'events' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('events')}
                  className="px-3"
                >
                  Events
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards List */}
      <Card>
        <CardHeader>
          <CardTitle>Scanned Contacts ({cards.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {cards.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <div className="text-4xl mb-4">📱</div>
              <p>No business cards scanned yet.</p>
              <Button 
                variant="outline" 
                onClick={openScanModal}
                className="mt-4"
              >
                Scan Your First Card
              </Button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cards.filter(card => {
                // Filter by event
                if (filterEvent && filterEvent !== 'all' && card.event_id !== filterEvent) return false;
                // Filter by owner (admin only)
                if (user && isAdmin(user) && filterOwner && filterOwner !== 'all' && card.created_by_name !== filterOwner) return false;
                return true;
              }).map((card) => (
                <Card key={card.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    {/* Card Image Thumbnail */}
                    {(card.image_url || card.cropped_image_url) ? (
                      <div className="mb-3">
                        <img 
                          src={getImageUrl(card.image_url || card.cropped_image_url)}
                          alt="Business card"
                          className="w-full h-24 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => openImageModal(card)}
                          title="Click to view full size"
                          onError={(e) => {
                            const primaryUrl = card.image_url || card.cropped_image_url;
                            console.log(`Failed to load primary image, trying fallback: ${getImageUrl(primaryUrl)}`);
                            
                            // Try the other image URL if available
                            const fallbackUrl = card.image_url ? card.cropped_image_url : card.image_url;
                            if (fallbackUrl) {
                              e.target.src = getImageUrl(fallbackUrl);
                              e.target.onError = () => {
                                console.log(`Both image URLs failed for card ${card.id}`);
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              };
                            } else {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }
                          }}
                        />
                        <div className="w-full h-24 bg-slate-100 rounded border flex items-center justify-center text-slate-400" style={{ display: 'none' }}>
                          Image unavailable
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <div className="w-full h-24 bg-slate-100 rounded border flex items-center justify-center text-slate-400">
                          No image available
                        </div>
                      </div>
                    )}
                    
                    {/* Owner Initials for Admin */}
                    {user && isAdmin(user) && card.created_by_name && (
                      <div className="mb-2">
                        <Badge 
                          variant="secondary" 
                          className="text-xs font-semibold px-2 py-1" 
                          title={`Scanned by: ${card.created_by_name}`}
                        >
                          {getUserInitials(card.created_by_name)}
                        </Badge>
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">
                          {card.name || 'Unknown Contact'}
                        </h3>
                        {card.position && (
                          <p className="text-sm text-slate-600">{card.position}</p>
                        )}
                        {card.company && (
                          <p className="text-sm text-slate-500">{card.company}</p>
                        )}
                      </div>
                      {card.ocr_confidence !== null && (
                        <Badge variant="outline" className="text-xs">
                          {Math.round(card.ocr_confidence * 100)}%
                        </Badge>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      {card.email && (
                        <div className="flex items-center space-x-1">
                          <span>📧</span>
                          <span className="truncate">{card.email}</span>
                        </div>
                      )}
                      {card.website && (
                        <div className="flex items-center space-x-1">
                          <span>🌐</span>
                          <a 
                            href={card.website.startsWith('http') ? card.website : `https://${card.website}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline truncate"
                          >
                            {card.website}
                          </a>
                        </div>
                      )}
                      {card.country && (
                        <div className="flex items-center space-x-1">
                          <span>🌍</span>
                          <span>{card.country}</span>
                        </div>
                      )}
                      
                      {/* Email Status Checkboxes */}
                      <div className="flex items-center gap-4 pt-2 border-t">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={card.email_sent || false}
                            onChange={(e) => updateEmailStatus(card.id, 'email_sent', e.target.checked)}
                            className="w-4 h-4 cursor-pointer"
                          />
                          <span className="text-xs">Email Sent</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={card.email_responded || false}
                            onChange={(e) => updateEmailStatus(card.id, 'email_responded', e.target.checked)}
                            className="w-4 h-4 cursor-pointer"
                          />
                          <span className="text-xs">Response</span>
                        </label>
                      </div>
                    </div>

                    <div className="flex space-x-2 flex-wrap">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openThankYouEmailDialog(card)}
                        className="bg-blue-50 hover:bg-blue-100"
                      >
                        📧 Compose Email
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditCard(card)}
                        data-testid={`edit-card-${card.id}`}
                        className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                      >
                        ✏️ Edit Contact
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => enhanceCard(card.id)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        data-testid={`enhance-card-${card.id}`}
                      >
                        🔍 Enhance
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteCard(card.id, card.name || 'contact')}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-card-${card.id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : viewMode === 'table' ? (
            // Table view
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-center py-2 px-2 w-16">Card</th>
                    <th className="text-left py-2 px-2">Contact</th>
                    <th className="text-left py-2 px-2">Company</th>
                    <th className="text-left py-2 px-2">Event</th>
                    <th className="text-left py-2 px-2">Interests</th>
                    <th className="text-center py-2 px-2">Email Sent</th>
                    <th className="text-center py-2 px-2">Response</th>
                    <th className="text-left py-2 px-2">Mailing Lists</th>
                    {user && isAdmin(user) && (
                      <th className="text-left py-2 px-2">Scanned By</th>
                    )}
                    <th className="text-center py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cards.filter(card => {
                    // Filter by event
                    if (filterEvent && filterEvent !== 'all' && card.event_id !== filterEvent) return false;
                    // Filter by owner (admin only)
                    if (user && isAdmin(user) && filterOwner && filterOwner !== 'all' && card.created_by_name !== filterOwner) return false;
                    return true;
                  }).map((card) => (
                    <tr key={card.id} className="border-b hover:bg-slate-50">
                      {/* Card Thumbnail */}
                      <td className="py-2 px-2">
                        {(card.image_url || card.cropped_image_url) ? (
                          <>
                            <img 
                              src={getImageUrl(card.image_url || card.cropped_image_url)}
                              alt="Card"
                              className="w-12 h-8 object-cover rounded border cursor-pointer hover:opacity-80"
                              onClick={() => openImageModal(card)}
                              title="Click to view full size"
                              onError={(e) => {
                                const primaryUrl = card.image_url || card.cropped_image_url;
                                console.log(`Table view: Failed to load primary image, trying fallback: ${getImageUrl(primaryUrl)}`);
                                
                                // Try the other image URL if available
                                const fallbackUrl = card.image_url ? card.cropped_image_url : card.image_url;
                                if (fallbackUrl) {
                                  e.target.src = getImageUrl(fallbackUrl);
                                  e.target.onError = () => {
                                    console.log(`Table view: Both image URLs failed for card ${card.id}`);
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  };
                                } else {
                                  e.target.style.display = 'none';
                                  e.target.nextElementSibling.style.display = 'flex';
                                }
                              }}
                            />
                            <div className="w-12 h-8 bg-slate-100 rounded border flex items-center justify-center text-xs text-slate-400" style={{ display: 'none' }}>
                              N/A
                            </div>
                          </>
                        ) : (
                          <div className="w-12 h-8 bg-slate-100 rounded border flex items-center justify-center text-xs text-slate-400">
                            N/A
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <div>
                          <div className="font-medium">{card.name || 'Unknown'}</div>
                          {card.email && (
                            <div className="text-xs text-slate-500">📧 {card.email}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2">
                        <div>
                          <div className="font-medium">{card.company || 'Unknown Company'}</div>
                          {card.website && (
                            <div className="text-xs text-slate-500">🌐 {card.website}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2">
                        <div className="text-xs">
                          {events.find(e => e.id === card.event_id)?.name || 'No Event'}
                        </div>
                      </td>
                      <td className="py-2 px-2">
                        <div className="text-xs space-y-1">
                          {(() => {
                            let interestsList = [];
                            
                            // Handle different interest data formats
                            if (card.interests) {
                              if (Array.isArray(card.interests)) {
                                // Process array elements
                                interestsList = card.interests.filter(interest => {
                                  // Filter out UUIDs (strings that look like: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
                                  if (typeof interest === 'string') {
                                    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(interest);
                                    return !isUUID && interest.trim().length > 2;
                                  }
                                  return false;
                                });
                              } else if (typeof card.interests === 'string') {
                                // Clean concatenated string by removing UUIDs and extracting readable parts
                                const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
                                
                                // Replace UUIDs with separators and split
                                let cleanedString = card.interests
                                  .replace(uuidPattern, '|||') // Replace UUIDs with triple pipe
                                  .split('|||') // Split by triple pipe
                                  .map(part => part.trim()) // Trim whitespace
                                  .filter(part => part && part.length > 2) // Filter empty and too short
                                  .flatMap(part => 
                                    // Further split by common separators
                                    part.split(/[,;]/).map(s => s.trim()).filter(s => s && s.length > 2)
                                  );
                                
                                interestsList = [...new Set(cleanedString)]; // Remove duplicates
                              }
                            }
                            
                            if (interestsList.length > 0) {
                              return interestsList.map((interest, idx) => {
                                // Convert interest ID to name if needed
                                let displayInterest = interest;
                                if (typeof interest === 'string' && interest.includes('-') && interest.length > 30 && card.event_id) {
                                  // This looks like a UUID, try to convert it
                                  const cardEvent = events.find(e => e.id === card.event_id);
                                  if (cardEvent && cardEvent.areas_of_interest) {
                                    const interestObj = cardEvent.areas_of_interest.find(ai => ai.id === interest);
                                    displayInterest = interestObj ? interestObj.label : interest;
                                  }
                                }
                                return (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {displayInterest}
                                  </Badge>
                                );
                              });
                            } else {
                              return <span className="text-slate-400">None</span>;
                            }
                          })()}
                          {card.notes && (
                            <div className="text-slate-500 italic">{card.notes}</div>
                          )}
                        </div>
                      </td>
                      {/* Email Sent Checkbox */}
                      <td className="py-2 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={card.email_sent || false}
                          onChange={(e) => updateEmailStatus(card.id, 'email_sent', e.target.checked)}
                          className="w-4 h-4 cursor-pointer"
                          title="Mark if email has been sent"
                        />
                      </td>
                      {/* Email Response Checkbox */}
                      <td className="py-2 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={card.email_responded || false}
                          onChange={(e) => updateEmailStatus(card.id, 'email_responded', e.target.checked)}
                          className="w-4 h-4 cursor-pointer"
                          title="Mark if contact has responded"
                        />
                      </td>
                      {/* Mailing Lists Dropdown */}
                      <td className="py-2 px-2">
                        {card.email ? (
                          <select
                            className="w-full text-xs p-1 border rounded"
                            onChange={(e) => {
                              const listId = e.target.value;
                              if (listId) {
                                addSubscribers(listId, null, {
                                  email: card.email,
                                  first_name: card.name?.split(' ')[0] || '',
                                  last_name: card.name?.split(' ').slice(1).join(' ') || ''
                                });
                                e.target.value = ''; // Reset dropdown
                              }
                            }}
                          >
                            <option value="">Add to list...</option>
                            {emailLists.map(list => (
                              <option key={list.id} value={list.id}>
                                {list.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-slate-400">No email</span>
                        )}
                      </td>
                      {user && isAdmin(user) && (
                        <td className="py-2 px-2">
                          <Badge 
                            variant="secondary" 
                            className="text-xs font-semibold px-2 py-1" 
                            title={`Scanned by: ${card.created_by_name || 'Unknown User'}`}
                          >
                            {getUserInitials(card.created_by_name)}
                          </Badge>
                        </td>
                      )}
                      <td className="py-2 px-2 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openThankYouEmailDialog(card)}
                            className="bg-blue-50 hover:bg-blue-100"
                          >
                            📧 Compose Email
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditCard(card)}
                            className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                          >
                            ✏️ Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteCard(card.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            // Events view
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-2 px-2">Event Name</th>
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-left py-2 px-2">Location</th>
                    <th className="text-left py-2 px-2">Cards Count</th>
                    <th className="text-left py-2 px-2">Created</th>
                    <th className="text-center py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => {
                    const eventCardsCount = cards.filter(card => card.event_id === event.id).length;
                    return (
                      <tr key={event.id} className="border-b hover:bg-slate-50">
                        <td className="py-2 px-2 font-medium">{event.name}</td>
                        <td className="py-2 px-2">{event.date ? new Date(event.date).toLocaleDateString() : '-'}</td>
                        <td className="py-2 px-2">{event.location || '-'}</td>
                        <td className="py-2 px-2">
                          <Badge variant="outline">{eventCardsCount} cards</Badge>
                        </td>
                        <td className="py-2 px-2 text-xs text-slate-500">
                          {event.created_at ? new Date(event.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-2 px-2 text-center">
                          <div className="flex gap-1 justify-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setFilterEvent(event.id);
                                setViewMode('grid'); // Switch to grid view to show the filtered cards
                                // Scroll to the top of the business card scanner
                                setTimeout(() => {
                                  document.querySelector('.space-y-6').scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }, 100);
                              }}
                            >
                              View Cards
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scanning Modal */}
      <Dialog open={showScanModal} onOpenChange={setShowScanModal}>
        <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Scan Business Card</DialogTitle>
            <DialogDescription>
              Upload or capture a business card image for AI processing
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Event Selection */}
            <div className="space-y-2">
              <Label>Associate with Event (Optional)</Label>
              <Select value={selectedEvent} onValueChange={handleEventSelection}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-event">No Event</SelectItem>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Image Capture */}
            {!capturedImage && (
              <div className="space-y-2">
                <Label>Capture or Upload Business Card</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageCapture}
                  disabled={isProcessing}
                  className="cursor-pointer"
                />
                <p className="text-xs text-slate-500">
                  Select a business card image from your device.
                </p>
              </div>
            )}

            {/* Image Preview */}
            {capturedImage && (
              <div className="space-y-4">
                <div>
                  <Label>Captured Image</Label>
                  <div className="mt-2 border rounded-lg overflow-hidden">
                    <img 
                      src={capturedImage} 
                      alt="Captured business card" 
                      className="w-full max-h-64 object-contain bg-slate-50"
                    />
                  </div>
                </div>

                {isProcessing && (
                  <div className="flex items-center justify-center p-6 bg-blue-50 rounded-lg">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                    <span className="text-blue-700">AI is extracting contact information...</span>
                  </div>
                )}

                {/* Extracted Data Form */}
                {extractedData && !isProcessing && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">Review & Edit Contact Information</Label>
                      {extractedData.confidence && (
                        <Badge variant="outline">
                          {Math.round(extractedData.confidence * 100)}% confidence
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Name *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Position</Label>
                        <Input
                          value={formData.position}
                          onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                          placeholder="Job title"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Company</Label>
                        <Input
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          placeholder="Company name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="email@company.com"
                          type="email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="Phone number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Mobile</Label>
                        <Input
                          value={formData.mobile}
                          onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                          placeholder="Mobile number (if different)"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Address</Label>
                        <Input
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          placeholder="Full address"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Country</Label>
                        <Input
                          value={formData.country}
                          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                          placeholder="Country"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Website</Label>
                        <Input
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                          placeholder="www.company.com"
                        />
                      </div>
                    </div>
                    
                    {/* Areas of Interest (only show if event is selected) */}
                    {selectedEvent !== 'no-event' && eventInterests.length > 0 && (
                      <div className="space-y-4 p-4 border rounded-lg bg-slate-50">
                        <Label className="text-base font-semibold">Areas of Interest - {selectedEventDetails?.name}</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {eventInterests.map((interest) => (
                            <div key={interest.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`interest-${interest.id}`}
                                checked={selectedInterests.includes(interest.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedInterests([...selectedInterests, interest.id]);
                                  } else {
                                    setSelectedInterests(selectedInterests.filter(id => id !== interest.id));
                                  }
                                }}
                              />
                              <Label htmlFor={`interest-${interest.id}`} className="text-sm">
                                {interest.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="other-interest">Other (please specify)</Label>
                          <Input
                            id="other-interest"
                            value={otherInterest}
                            onChange={(e) => setOtherInterest(e.target.value)}
                            placeholder="Other areas of interest..."
                          />
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Input
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Additional notes..."
                      />
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <Button 
                        onClick={saveCard}
                        className="flex-1 bg-sky-600 hover:bg-sky-700"
                      >
                        Save Contact
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setCapturedImage(null);
                          setCroppedImageUrl(null);
                          setExtractedData(null);
                        }}
                      >
                        Scan Different Card
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Card Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>
              Update contact information
            </DialogDescription>
          </DialogHeader>
          
          {/* Business Card Image Preview */}
          {editingCard && (editingCard.image_url || editingCard.cropped_image_url) && (
            <div className="mb-4">
              <Label className="text-sm font-medium mb-2 block">Business Card Preview</Label>
              <div className="border rounded-lg p-2 bg-gray-50">
                <img 
                  src={getImageUrl(editingCard.image_url || editingCard.cropped_image_url)}
                  alt="Business card preview"
                  className="w-full max-h-48 object-contain rounded cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => window.open(getImageUrl(editingCard.image_url || editingCard.cropped_image_url), '_blank')}
                  title="Click to view full size"
                  onError={(e) => {
                    const primaryUrl = editingCard.image_url || editingCard.cropped_image_url;
                    console.log(`Failed to load primary image, trying fallback: ${getImageUrl(primaryUrl)}`);
                    
                    // Try the other image URL if available
                    const fallbackUrl = editingCard.image_url ? editingCard.cropped_image_url : editingCard.image_url;
                    if (fallbackUrl) {
                      e.target.src = getImageUrl(fallbackUrl);
                      e.target.onError = () => {
                        console.log(`Both image URLs failed for editing card ${editingCard.id}`);
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      };
                    } else {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }
                  }}
                />
                <div style={{ display: 'none' }} className="text-center p-8 text-gray-500 text-sm">
                  Card image not available
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Click image to view full size. Verify extracted data matches the card above.
              </p>
            </div>
          )}
          
          {/* Replace/Update Card Image */}
          <div className="space-y-2 mb-4">
            <Label htmlFor="edit-card-image" className="text-sm font-medium">
              Replace Card Image
            </Label>
            <Input
              id="edit-card-image"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setEditCard({ ...editCard, newImage: file });
                }
              }}
              className="cursor-pointer"
            />
            <p className="text-xs text-gray-500">
              Upload a new business card image to replace the current one (without re-scanning)
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editCard.name}
                  onChange={(e) => setEditCard({ ...editCard, name: e.target.value })}
                  data-testid="edit-card-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-position">Position</Label>
                <Input
                  id="edit-position"
                  value={editCard.position}
                  onChange={(e) => setEditCard({ ...editCard, position: e.target.value })}
                  data-testid="edit-card-position-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-company">Company</Label>
              <Input
                id="edit-company"
                value={editCard.company}
                onChange={(e) => setEditCard({ ...editCard, company: e.target.value })}
                data-testid="edit-card-company-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editCard.email}
                  onChange={(e) => setEditCard({ ...editCard, email: e.target.value })}
                  data-testid="edit-card-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editCard.phone}
                  onChange={(e) => setEditCard({ ...editCard, phone: e.target.value })}
                  data-testid="edit-card-phone-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-mobile">Mobile</Label>
                <Input
                  id="edit-mobile"
                  value={editCard.mobile}
                  onChange={(e) => setEditCard({ ...editCard, mobile: e.target.value })}
                  placeholder="Mobile number (if different)"
                  data-testid="edit-card-mobile-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-website">Website</Label>
                <Input
                  id="edit-website"
                  value={editCard.website}
                  onChange={(e) => setEditCard({ ...editCard, website: e.target.value })}
                  placeholder="www.company.com"
                  data-testid="edit-card-website-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Input
                id="edit-address"
                value={editCard.address || ''}
                onChange={(e) => setEditCard({ ...editCard, address: e.target.value })}
                placeholder="Company address"
                data-testid="edit-card-address-input"
              />
            </div>

            {/* Event Selection for Edit */}
            <div className="space-y-2">
              <Label htmlFor="edit-event">Event</Label>
              <Select value={selectedEvent} onValueChange={handleEventSelection}>
                <SelectTrigger>
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-event">No Event</SelectItem>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Areas of Interest for Edit */}
            {selectedEvent !== 'no-event' && eventInterests.length > 0 && (
              <div className="space-y-4 p-4 border rounded-lg bg-slate-50">
                <Label className="text-base font-semibold">Areas of Interest - {selectedEventDetails?.name}</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {eventInterests.map((interest) => (
                    <div key={interest.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-interest-${interest.id}`}
                        checked={selectedInterests.includes(interest.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedInterests([...selectedInterests, interest.id]);
                          } else {
                            setSelectedInterests(selectedInterests.filter(id => id !== interest.id));
                          }
                        }}
                      />
                      <Label 
                        htmlFor={`edit-interest-${interest.id}`} 
                        className="text-sm font-normal cursor-pointer"
                      >
                        {interest.label}
                      </Label>
                    </div>
                  ))}
                </div>
                
                {/* Other Interest for Edit */}
                <div className="space-y-2">
                  <Label htmlFor="edit-other-interest">Other Interest</Label>
                  <Input
                    id="edit-other-interest"
                    value={otherInterest}
                    onChange={(e) => setOtherInterest(e.target.value)}
                    placeholder="Any other areas of interest..."
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Input
                id="edit-notes"
                value={editCard.notes}
                onChange={(e) => setEditCard({ ...editCard, notes: e.target.value })}
                placeholder="Additional notes..."
                data-testid="edit-card-notes-input"
              />
            </div>
            
            <Button 
              onClick={updateCard} 
              className="w-full bg-sky-600 hover:bg-sky-700"
              data-testid="edit-card-submit"
            >
              Update Contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Thank You Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-4xl h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Compose Email</DialogTitle>
            <DialogDescription>
              AI-generated personalized email for {thankYouEmailData.recipient_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-md">
              <h4 className="font-medium text-slate-900 mb-2">Contact Details</h4>
              <div className="text-sm text-slate-600 space-y-1">
                <p><strong>Name:</strong> {thankYouEmailData.recipient_name}</p>
                <p><strong>Company:</strong> {thankYouEmailData.company}</p>
                <p><strong>Event:</strong> {thankYouEmailData.event_name}</p>
                <p><strong>Interests:</strong> {thankYouEmailData.interests.length > 0 ? thankYouEmailData.interests.join(', ') : 'None specified'}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email-address">Email Address</Label>
              <Input
                id="email-address"
                type="email"
                value={thankYouEmailData.recipient_email}
                onChange={(e) => setThankYouEmailData({...thankYouEmailData, recipient_email: e.target.value})}
                placeholder="recipient@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email-subject">Subject</Label>
              <Input
                id="email-subject"
                value={thankYouEmailData.subject}
                onChange={(e) => setThankYouEmailData({...thankYouEmailData, subject: e.target.value})}
                placeholder="Email subject"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email-body">Message</Label>
              <textarea
                id="email-body"
                value={thankYouEmailData.body}
                onChange={(e) => setThankYouEmailData({...thankYouEmailData, body: e.target.value})}
                placeholder="Email body..."
                className="w-full h-80 p-3 border rounded-md resize-vertical"
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
                Cancel
              </Button>
              <Button onClick={async () => {
                try {
                  const response = await axios.post(`${API}/send-thank-you-email`, {
                    to: thankYouEmailData.recipient_email,
                    subject: thankYouEmailData.subject,
                    body: thankYouEmailData.body
                  });
                  
                  if (response.data.success) {
                    toast.success(response.data.message);
                    setShowEmailDialog(false);
                  } else {
                    toast.error(response.data.message);
                  }
                } catch (error) {
                  toast.error('Failed to send email');
                }
              }}>
                Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Business Card View</DialogTitle>
            <DialogDescription>
              {selectedCardImage ? `${selectedCardImage.name || 'Contact'} - ${selectedCardImage.company || 'Company'}` : 'Business Card'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center">
            {selectedCardImage && (selectedCardImage.image_url || selectedCardImage.cropped_image_url) ? (
              <img 
                src={getImageUrl(selectedCardImage.image_url || selectedCardImage.cropped_image_url)}
                alt="Business card full size"
                className="max-w-full max-h-[70vh] object-contain rounded border"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : null}
            <div style={{ display: 'none' }} className="text-center p-8 bg-gray-100 rounded text-gray-500">
              Card image not available
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Contact Entry Dialog */}
      <Dialog open={showManualEntryDialog} onOpenChange={setShowManualEntryDialog}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Contact Manually</DialogTitle>
            <DialogDescription>
              Enter business card information manually
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="manual-name">Name</Label>
                <Input
                  id="manual-name"
                  value={manualEntryData.name}
                  onChange={(e) => setManualEntryData({ ...manualEntryData, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manual-position">Position</Label>
                <Input
                  id="manual-position"
                  value={manualEntryData.position}
                  onChange={(e) => setManualEntryData({ ...manualEntryData, position: e.target.value })}
                  placeholder="Job title"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="manual-company">Company</Label>
              <Input
                id="manual-company"
                value={manualEntryData.company}
                onChange={(e) => setManualEntryData({ ...manualEntryData, company: e.target.value })}
                placeholder="Company name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="manual-email">Email</Label>
                <Input
                  id="manual-email"
                  type="email"
                  value={manualEntryData.email}
                  onChange={(e) => setManualEntryData({ ...manualEntryData, email: e.target.value })}
                  placeholder="email@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manual-phone">Phone</Label>
                <Input
                  id="manual-phone"
                  value={manualEntryData.phone}
                  onChange={(e) => setManualEntryData({ ...manualEntryData, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="manual-mobile">Mobile</Label>
                <Input
                  id="manual-mobile"
                  value={manualEntryData.mobile}
                  onChange={(e) => setManualEntryData({ ...manualEntryData, mobile: e.target.value })}
                  placeholder="Mobile number (if different)"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manual-website">Website</Label>
                <Input
                  id="manual-website"
                  value={manualEntryData.website}
                  onChange={(e) => setManualEntryData({ ...manualEntryData, website: e.target.value })}
                  placeholder="www.company.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="manual-address">Address</Label>
              <Input
                id="manual-address"
                value={manualEntryData.address}
                onChange={(e) => setManualEntryData({ ...manualEntryData, address: e.target.value })}
                placeholder="Company address"
              />
            </div>

            {/* Event Selection for Manual Entry */}
            <div className="space-y-2">
              <Label htmlFor="manual-event">Event</Label>
              <Select value={selectedEvent} onValueChange={handleEventSelection}>
                <SelectTrigger>
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-event">No Event</SelectItem>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Areas of Interest for Manual Entry */}
            {selectedEvent !== 'no-event' && eventInterests.length > 0 && (
              <div className="space-y-4 p-4 border rounded-lg bg-slate-50">
                <Label className="text-base font-semibold">Areas of Interest - {selectedEventDetails?.name}</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {eventInterests.map((interest) => (
                    <div key={interest.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`manual-interest-${interest.id}`}
                        checked={selectedInterests.includes(interest.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedInterests([...selectedInterests, interest.id]);
                          } else {
                            setSelectedInterests(selectedInterests.filter(id => id !== interest.id));
                          }
                        }}
                      />
                      <Label 
                        htmlFor={`manual-interest-${interest.id}`} 
                        className="text-sm font-normal cursor-pointer"
                      >
                        {interest.label}
                      </Label>
                    </div>
                  ))}
                </div>
                
                {/* Other Interest for Manual Entry */}
                <div className="space-y-2">
                  <Label htmlFor="manual-other-interest">Other Interest</Label>
                  <Input
                    id="manual-other-interest"
                    value={otherInterest}
                    onChange={(e) => setOtherInterest(e.target.value)}
                    placeholder="Any other areas of interest..."
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="manual-notes">Notes</Label>
              <Input
                id="manual-notes"
                value={manualEntryData.notes}
                onChange={(e) => setManualEntryData({ ...manualEntryData, notes: e.target.value })}
                placeholder="Additional notes..."
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={saveManualContact} 
                className="flex-1 bg-sky-600 hover:bg-sky-700"
              >
                Add Contact
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setShowManualEntryDialog(false);
                  setManualEntryData({
                    name: '',
                    company: '',
                    position: '',
                    email: '',
                    phone: '',
                    mobile: '',
                    website: '',
                    address: '',
                    notes: ''
                  });
                  setSelectedEvent('no-event');
                  setSelectedInterests([]);
                  setOtherInterest('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// GE Management Component
const GEManagement = () => {
  const [ges, setGes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingGE, setEditingGE] = useState(null);
  const [newGE, setNewGE] = useState({
    code: '',
    name: ''
  });
  const [editGE, setEditGE] = useState({
    code: '',
    name: '',
    status: 'active'
  });

  useEffect(() => {
    fetchGEs();
  }, []);

  const fetchGEs = async () => {
    try {
      const response = await axios.get(`${API}/ges`);
      setGes(response.data);
    } catch (error) {
      toast.error('Failed to load GEs');
    } finally {
      setLoading(false);
    }
  };

  const createGE = async () => {
    try {
      // Convert code to uppercase
      const geData = {
        ...newGE,
        code: newGE.code.toUpperCase()
      };
      
      await axios.post(`${API}/ges`, geData);
      toast.success('GE created successfully');
      setShowCreateDialog(false);
      setNewGE({ code: '', name: '' });
      fetchGEs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create GE');
    }
  };

  const openEditGE = (ge) => {
    setEditingGE(ge);
    setEditGE({
      code: ge.code,
      name: ge.name,
      status: ge.status
    });
    setShowEditDialog(true);
  };

  const updateGE = async () => {
    try {
      await axios.put(`${API}/ges/${editingGE.id}`, editGE);
      toast.success('GE updated successfully');
      setShowEditDialog(false);
      setEditingGE(null);
      setEditGE({ code: '', name: '', status: 'active' });
      fetchGEs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update GE');
    }
  };

  const deleteGE = async (geId, geCode) => {
    if (window.confirm(`Are you sure you want to delete GE "${geCode}"? This action cannot be undone.`)) {
      try {
        await axios.delete(`${API}/ges/${geId}`);
        toast.success('GE deleted successfully');
        fetchGEs();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete GE');
      }
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">GE Administration</h2>
          <p className="text-slate-600">Manage project codes and assignments</p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-sky-600 hover:bg-sky-700" data-testid="create-ge-button">
              Create GE
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New GE</DialogTitle>
              <DialogDescription>
                Add a new project code (6-10 characters, uppercase)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ge-code">GE Code</Label>
                <Input
                  id="ge-code"
                  placeholder="e.g., PROJ001 or VAS2024"
                  value={newGE.code}
                  onChange={(e) => setNewGE({ ...newGE, code: e.target.value.toUpperCase() })}
                  maxLength={10}
                  data-testid="ge-code-input"
                />
                <p className="text-xs text-slate-500">6-10 characters, letters and numbers only</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ge-name">Project Name</Label>
                <Input
                  id="ge-name"
                  placeholder="e.g., Terminal Operations Phase 1"
                  value={newGE.name}
                  onChange={(e) => setNewGE({ ...newGE, name: e.target.value })}
                  maxLength={100}
                  data-testid="ge-name-input"
                />
              </div>
              <Button 
                onClick={createGE} 
                className="w-full bg-sky-600 hover:bg-sky-700"
                data-testid="create-ge-submit"
              >
                Create GE
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit GE Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit GE: {editingGE?.code}</DialogTitle>
              <DialogDescription>
                Update project information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-ge-code">GE Code</Label>
                <Input
                  id="edit-ge-code"
                  value={editGE.code}
                  onChange={(e) => setEditGE({ ...editGE, code: e.target.value.toUpperCase() })}
                  maxLength={10}
                  data-testid="edit-ge-code-input"
                />
                <p className="text-xs text-slate-500">6-10 characters, letters and numbers only</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-ge-name">Project Name</Label>
                <Input
                  id="edit-ge-name"
                  value={editGE.name}
                  onChange={(e) => setEditGE({ ...editGE, name: e.target.value })}
                  maxLength={100}
                  data-testid="edit-ge-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-ge-status">Status</Label>
                <Select value={editGE.status} onValueChange={(value) => setEditGE({ ...editGE, status: value })}>
                  <SelectTrigger data-testid="edit-ge-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={updateGE} 
                className="w-full bg-sky-600 hover:bg-sky-700"
                data-testid="edit-ge-submit"
              >
                Update GE
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Project Codes (GEs)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {ges.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No GEs created yet. Click "Create GE" to add your first project code.
              </div>
            ) : (
              ges.map((ge) => (
                <div key={ge.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`ge-item-${ge.id}`}>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-orange-100 text-orange-800 rounded-lg flex items-center justify-center font-bold text-sm">
                        {ge.code.substring(0, 4)}
                      </div>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{ge.code}</p>
                      <p className="text-slate-600">{ge.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge 
                      variant={ge.status === 'active' ? 'default' : 'secondary'}
                      className={ge.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                    >
                      {ge.status}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditGE(ge)}
                      data-testid={`edit-ge-${ge.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteGE(ge.id, ge.code)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-ge-${ge.id}`}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Event Management Component
const EventManagement = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // View states
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showInterestsDialog, setShowInterestsDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEventInterests, setSelectedEventInterests] = useState([]);
  const [newInterest, setNewInterest] = useState('');
  const [newEvent, setNewEvent] = useState({
    name: '',
    date: '',
    location: ''
  });
  const [editEvent, setEditEvent] = useState({
    name: '',
    date: '',
    location: ''
  });

  useEffect(() => {
    fetchEvents();
    if (user) {
      fetchEmailLists();
    }
  }, [user]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };


  const fetchEmailLists = async () => {
    if (!user || !isAdmin(user)) return;
    try {
      const response = await axios.get(`${API}/email-lists`);
      setEmailLists(response.data);
    } catch (error) {
      console.error('Failed to fetch email lists:', error);
    }
  };

  const addSubscribers = async (listId, file, singleEmail) => {
    try {
      const formData = new FormData();
      if (file) {
        formData.append('subscribers_csv', file);
      } else if (singleEmail) {
        formData.append('email', singleEmail.email);
        formData.append('first_name', singleEmail.first_name || '');
        formData.append('last_name', singleEmail.last_name || '');
      }

      const response = await axios.post(`${API}/email-lists/${listId}/subscribers`, formData);
      toast.success(`Added ${response.data.added} subscriber(s)`);
      fetchEmailLists();
    } catch (error) {
      toast.error('Failed to add subscribers');
    }
  };


  const createEvent = async () => {
    try {
      const eventData = {
        ...newEvent,
        date: new Date(newEvent.date).toISOString()
      };
      
      await axios.post(`${API}/events`, eventData);
      toast.success('Event created successfully');
      setShowCreateDialog(false);
      setNewEvent({ name: '', date: '', location: '' });
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEditEvent({
      name: event.name,
      date: event.date.split('T')[0], // Convert to date input format
      location: event.location
    });
    setShowEditDialog(true);
  };

  const updateEvent = async () => {
    try {
      const eventData = {
        name: editEvent.name,
        date: new Date(editEvent.date).toISOString(),
        location: editEvent.location
      };
      
      await axios.put(`${API}/events/${editingEvent.id}`, eventData);
      toast.success('Event updated successfully');
      setShowEditDialog(false);
      setEditingEvent(null);
      setEditEvent({ name: '', date: '', location: '' });
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    }
  };

  const deleteEvent = async (eventId, eventName) => {
    if (window.confirm(`Are you sure you want to delete "${eventName}"?`)) {
      try {
        await axios.delete(`${API}/events/${eventId}`);
        toast.success('Event deleted successfully');
        fetchEvents();
      } catch (error) {
        toast.error('Failed to delete event');
      }
    }
  };

  const openInterestsDialog = async (event) => {
    setEditingEvent(event);
    try {
      const response = await axios.get(`${API}/events/${event.id}/interests`);
      setSelectedEventInterests(response.data);
    } catch (error) {
      setSelectedEventInterests([]);
    }
    setShowInterestsDialog(true);
  };

  const addInterest = async () => {
    if (!newInterest.trim()) return;
    
    try {
      await axios.post(`${API}/events/${editingEvent.id}/interests`, {
        label: newInterest.trim()
      });
      
      // Refresh interests
      const response = await axios.get(`${API}/events/${editingEvent.id}/interests`);
      setSelectedEventInterests(response.data);
      setNewInterest('');
      toast.success('Interest added successfully');
    } catch (error) {
      toast.error('Failed to add interest');
    }
  };

  const deleteInterest = async (interestId) => {
    try {
      await axios.delete(`${API}/events/interests/${interestId}`);
      
      // Refresh interests
      const response = await axios.get(`${API}/events/${editingEvent.id}/interests`);
      setSelectedEventInterests(response.data);
      toast.success('Interest removed successfully');
    } catch (error) {
      toast.error('Failed to remove interest');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Event Management</h3>
          <p className="text-slate-600">Manage events for business card scanning</p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-sky-600 hover:bg-sky-700" data-testid="create-event-button">
              Create Event
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Event</DialogTitle>
              <DialogDescription>
                Add a new event for organizing contacts
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="event-name">Event Name</Label>
                <Input
                  id="event-name"
                  placeholder="e.g., Tech Conference 2024"
                  value={newEvent.name}
                  onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                  data-testid="event-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="event-date">Date</Label>
                <Input
                  id="event-date"
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  data-testid="event-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="event-location">Location</Label>
                <Input
                  id="event-location"
                  placeholder="e.g., Copenhagen Convention Center"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  data-testid="event-location-input"
                />
              </div>
              <Button 
                onClick={createEvent} 
                className="w-full bg-sky-600 hover:bg-sky-700"
                data-testid="create-event-submit"
              >
                Create Event
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Event Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Event</DialogTitle>
              <DialogDescription>
                Update event information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-event-name">Event Name</Label>
                <Input
                  id="edit-event-name"
                  value={editEvent.name}
                  onChange={(e) => setEditEvent({ ...editEvent, name: e.target.value })}
                  data-testid="edit-event-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-event-date">Date</Label>
                <Input
                  id="edit-event-date"
                  type="date"
                  value={editEvent.date}
                  onChange={(e) => setEditEvent({ ...editEvent, date: e.target.value })}
                  data-testid="edit-event-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-event-location">Location</Label>
                <Input
                  id="edit-event-location"
                  value={editEvent.location}
                  onChange={(e) => setEditEvent({ ...editEvent, location: e.target.value })}
                  data-testid="edit-event-location-input"
                />
              </div>
              <Button 
                onClick={updateEvent} 
                className="w-full bg-sky-600 hover:bg-sky-700"
                data-testid="edit-event-submit"
              >
                Update Event
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Events ({events.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {/* View Toggle */}
          <div className="flex items-center gap-2 mb-4">
            <Label>View:</Label>
            <div className="flex bg-gray-100 rounded-md p-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="px-3"
              >
                Grid
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className="px-3"
              >
                Table
              </Button>
            </div>
          </div>
          
          {events.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No events created yet. Events help organize business cards by occasion.
            </div>
          ) : viewMode === 'grid' ? (
            <div className="space-y-4">
              {events.map((event) => (
                <div key={event.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-semibold text-slate-900">{event.name}</h4>
                    <p className="text-sm text-slate-600">{new Date(event.date).toLocaleDateString()}</p>
                    <p className="text-sm text-slate-500">{event.location}</p>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditEvent(event)}
                      data-testid={`edit-event-${event.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openInterestsDialog(event)}
                      data-testid={`interests-event-${event.id}`}
                    >
                      Areas of Interest
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteEvent(event.id, event.name)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-event-${event.id}`}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Table view for events
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-2 px-2">Event Name</th>
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-left py-2 px-2">Location</th>
                    <th className="text-left py-2 px-2">Created</th>
                    <th className="text-center py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id} className="border-b hover:bg-slate-50">
                      <td className="py-2 px-2 font-medium">{event.name}</td>
                      <td className="py-2 px-2">{new Date(event.date).toLocaleDateString()}</td>
                      <td className="py-2 px-2">{event.location}</td>
                      <td className="py-2 px-2 text-xs text-slate-500">
                        {event.created_at ? new Date(event.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditEvent(event)}
                            data-testid={`edit-event-${event.id}`}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openInterestsDialog(event)}
                            data-testid={`interests-event-${event.id}`}
                            className="text-xs"
                          >
                            Interests
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteEvent(event.id, event.name)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-event-${event.id}`}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Areas of Interest Dialog */}
      <Dialog open={showInterestsDialog} onOpenChange={setShowInterestsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Areas of Interest - {editingEvent?.name}</DialogTitle>
            <DialogDescription>
              Manage areas of interest for this event
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add New Interest */}
            <div className="flex space-x-2">
              <Input
                placeholder="Add new interest..."
                value={newInterest}
                onChange={(e) => setNewInterest(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addInterest()}
              />
              <Button onClick={addInterest} disabled={!newInterest.trim()}>
                Add
              </Button>
            </div>
            
            {/* Current Interests */}
            <div className="space-y-2">
              <Label className="font-medium">Current Areas of Interest:</Label>
              {selectedEventInterests.length === 0 ? (
                <p className="text-sm text-slate-500">No areas of interest added yet</p>
              ) : (
                <div className="space-y-2">
                  {selectedEventInterests.map((interest) => (
                    <div key={interest.id} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <span className="text-sm">{interest.label}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteInterest(interest.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <Button onClick={() => setShowInterestsDialog(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// User Management Component
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [ges, setGes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role_id: '',
    ge_ids: [],
    event_ids: [],
    photo: null
  });
  const [editUser, setEditUser] = useState({
    name: '',
    email: '',
    role_id: '',
    status: 'active',
    ge_ids: [],
    event_ids: [],
    photo: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, gesRes, eventsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/roles`),
        axios.get(`${API}/ges`),
        axios.get(`${API}/events`)
      ]);
      
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setGes(gesRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async () => {
    try {
      // First create the user
      const response = await axios.post(`${API}/users`, {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role_id: newUser.role_id,
        ge_ids: newUser.ge_ids,
        event_ids: newUser.event_ids
      });
      
      // Then upload photo if provided
      if (newUser.photo) {
        const formData = new FormData();
        formData.append('file', newUser.photo);
        
        try {
          await axios.post(`${API}/upload-user-photo/${response.data.id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
          toast.error('User created but photo upload failed');
        }
      }
      
      toast.success('User created successfully');
      setShowCreateDialog(false);
      setNewUser({ name: '', email: '', password: '', role_id: '', ge_ids: [], event_ids: [], photo: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setEditUser({
      name: user.name,
      email: user.email,
      role_id: user.role_id,
      status: user.status,
      ge_ids: user.ge_ids || [],
      event_ids: user.event_ids || [],
      photo: null,
      password: '' // Always start with empty password
    });
    setShowEditDialog(true);
  };

  const updateUser = async () => {
    try {
      const updateData = { ...editUser };
      delete updateData.photo; // Handle photo separately
      
      // Remove password if empty (keep current password)
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password;
      }
      
      // Update user info
      await axios.put(`${API}/users/${editingUser.id}`, updateData);
      
      // Upload new photo if provided
      if (editUser.photo) {
        const formData = new FormData();
        formData.append('file', editUser.photo);
        
        try {
          await axios.post(`${API}/upload-user-photo/${editingUser.id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
          toast.error('User updated but photo upload failed');
        }
      }
      
      toast.success('User updated successfully');
      setShowEditDialog(false);
      setEditingUser(null);
      setEditUser({ name: '', email: '', role_id: '', status: 'active', ge_ids: [], event_ids: [], photo: null, password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to delete user "${userName}"?`)) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        toast.success('User deleted successfully');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">User Management</h2>
          <p className="text-slate-600">Manage users, roles, and permissions</p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-sky-600 hover:bg-sky-700" data-testid="create-user-button">
              Create User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Add a new user to the system
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="photo">Profile Photo</Label>
                <Input
                  id="photo"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewUser({ ...newUser, photo: e.target.files[0] })}
                  data-testid="user-photo-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@velocityairportsolutions.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select onValueChange={(value) => setNewUser({ ...newUser, role_id: value })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={createUser} 
                className="w-full bg-sky-600 hover:bg-sky-700"
                data-testid="create-user-submit"
              >
                Create User
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-photo">Profile Photo</Label>
                <Input
                  id="edit-photo"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEditUser({ ...editUser, photo: e.target.files[0] })}
                  data-testid="edit-user-photo-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editUser.name}
                  onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                  data-testid="edit-user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editUser.email}
                  placeholder="user@velocityairportsolutions.com"
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                  data-testid="edit-user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password">New Password (leave blank to keep current)</Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={editUser.password || ''}
                  placeholder="Enter new password or leave blank"
                  onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                  data-testid="edit-user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select value={editUser.role_id} onValueChange={(value) => setEditUser({ ...editUser, role_id: value })}>
                  <SelectTrigger data-testid="edit-user-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-status">Status</Label>
                <Select value={editUser.status} onValueChange={(value) => setEditUser({ ...editUser, status: value })}>
                  <SelectTrigger data-testid="edit-user-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={updateUser} 
                className="w-full bg-sky-600 hover:bg-sky-700"
                data-testid="edit-user-submit"
              >
                Update User
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {users.map((user) => (
              <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`user-item-${user.id}`}>
                <div className="flex items-center space-x-4">
                  <Avatar>
                    {user.photo_url && (
                      <AvatarImage 
                        src={`${BACKEND_URL}${user.photo_url}`} 
                        alt={user.name}
                      />
                    )}
                    <AvatarFallback className="bg-sky-100 text-sky-700">
                      {user.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-slate-900">{user.name}</p>
                    <p className="text-sm text-slate-600">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline">
                    {user.role?.name || 'No Role'}
                  </Badge>
                  <Badge 
                    variant={user.status === 'active' ? 'default' : 'secondary'}
                    className={user.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                  >
                    {user.status}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditUser(user)}
                    data-testid={`edit-user-${user.id}`}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteUser(user.id, user.name)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-user-${user.id}`}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Receipt Management Component
const ReceiptManagement = () => {
  const [receipts, setReceipts] = useState([]);
  const [ges, setGes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [showHandled, setShowHandled] = useState(false);
  const [filterGE, setFilterGE] = useState('all');
  
  // Receipt Scanning Modal States
  const [showScanModal, setShowScanModal] = useState(false);
  const [selectedGE, setSelectedGE] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'USD',
    seller: '',
    date: '',
    category: 'Other',
    description: '',
    notes: ''
  });

  // Receipt editing states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  
  // Image enlargement modal
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  useEffect(() => {
    fetchReceipts();
    fetchGEs();
  }, []);

  const fetchReceipts = async () => {
    try {
      const response = await axios.get(`${API}/receipts`);
      setReceipts(response.data);
    } catch (error) {
      toast.error('Failed to load receipts');
    } finally {
      setLoading(false);
    }
  };

  const toggleReceiptStatus = async (receiptId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'handled' ? 'active' : 'handled';
      await axios.put(`${API}/receipts/${receiptId}`, { status: newStatus });
      toast.success(`Receipt marked as ${newStatus}`);
      fetchReceipts();
    } catch (error) {
      toast.error('Failed to update receipt');
    }
  };

  const markAllReceiptsAsHandled = async () => {
    try {
      const unhandledReceipts = receipts.filter(r => r.status !== 'handled');
      const promises = unhandledReceipts.map(receipt => 
        axios.put(`${API}/receipts/${receipt.id}`, { status: 'handled' })
      );
      await Promise.all(promises);
      toast.success(`Marked ${unhandledReceipts.length} receipts as handled`);
      fetchReceipts();
    } catch (error) {
      toast.error('Failed to mark all receipts as handled');
    }
  };

  const fetchGEs = async () => {
    try {
      const response = await axios.get(`${API}/ges`);
      setGes(response.data);
    } catch (error) {
      console.error('Failed to load GEs:', error);
    }
  };

  const openScanModal = () => {
    setShowScanModal(true);
    setCapturedImage(null);
    setExtractedData(null);
    setFormData({
      amount: '',
      currency: 'USD',
      seller: '',
      date: '',
      category: 'Other',
      description: '',
      notes: ''
    });
  };

  const handleImageCapture = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/receipts/scan`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const extracted = response.data.extracted_data;
      setExtractedData(extracted);
      setCapturedImage(getImageUrl(response.data.image_url));
      
      // Pre-fill form with extracted data
      setFormData({
        amount: extracted.amount || '',
        currency: extracted.currency || 'USD',
        seller: extracted.seller || '',
        date: extracted.date ? extracted.date.split('T')[0] : '',
        category: extracted.category || 'Other',
        description: extracted.description || '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process receipt');
    } finally {
      setIsProcessing(false);
    }
  };

  const saveReceipt = async () => {
    if (!selectedGE) {
      toast.error('Please select a GE');
      return;
    }

    try {
      const receiptData = {
        ge_id: selectedGE,
        image_url: capturedImage, // Keep the full URL (Cloudinary or local)
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        date: formData.date ? new Date(formData.date).toISOString() : null
      };

      await axios.post(`${API}/receipts`, receiptData);
      toast.success('Receipt saved successfully!');
      
      // Reset and refresh
      setShowScanModal(false);
      setCapturedImage(null);
      setExtractedData(null);
      setSelectedGE('');
      setFormData({
        amount: '',
        currency: 'USD',
        seller: '',
        date: '',
        category: 'Other',
        description: '',
        notes: ''
      });
      fetchReceipts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save receipt');
    }
  };

  const openEditModal = (receipt) => {
    setEditingReceipt(receipt);
    setFormData({
      amount: receipt.amount || '',
      currency: receipt.currency || 'USD',
      seller: receipt.seller || '',
      date: receipt.date ? receipt.date.split('T')[0] : '',
      category: receipt.category || 'Other',
      description: receipt.description || '',
      notes: receipt.notes || ''
    });
    setShowEditModal(true);
  };

  const updateReceipt = async () => {
    try {
      const updateData = {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        date: formData.date ? new Date(formData.date).toISOString() : null
      };

      await axios.put(`${API}/receipts/${editingReceipt.id}`, updateData);
      toast.success('Receipt updated successfully!');
      
      setShowEditModal(false);
      setEditingReceipt(null);
      fetchReceipts();
    } catch (error) {
      toast.error('Failed to update receipt');
    }
  };

  const deleteReceipt = async (receiptId) => {
    if (!confirm('Are you sure you want to delete this receipt?')) return;

    try {
      await axios.delete(`${API}/receipts/${receiptId}`);
      toast.success('Receipt deleted successfully');
      fetchReceipts();
    } catch (error) {
      toast.error('Failed to delete receipt');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8">Loading receipts...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-900">Receipt Management</h2>
        <Button onClick={openScanModal} className="bg-green-600 hover:bg-green-700">
          <span className="mr-2">📄</span>
          Scan Receipt
        </Button>
      </div>

      {/* Filter Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Checkbox 
                id="show-handled"
                checked={showHandled}
                onCheckedChange={setShowHandled}
              />
              <Label htmlFor="show-handled">Show handled items</Label>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="filter-ge">Filter by GE:</Label>
              <Select value={filterGE} onValueChange={setFilterGE}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All GEs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All GEs</SelectItem>
                  {ges.map(ge => (
                    <SelectItem key={ge.id} value={ge.id}>
                      {ge.code} - {ge.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="secondary"
                size="sm"
                onClick={markAllReceiptsAsHandled}
                className="bg-blue-100 hover:bg-blue-200 text-blue-700"
              >
                Mark All as Handled
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receipts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {receipts.filter(receipt => {
          // Filter by handled status
          if (!showHandled && receipt.status === 'handled') return false;
          // Filter by GE
          if (filterGE && filterGE !== 'all' && receipt.ge_id !== filterGE) return false;
          return true;
        }).map((receipt) => {
          const geData = ges.find(g => g.id === receipt.ge_id);
          return (
            <Card key={receipt.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                {/* Receipt Image Thumbnail */}
                {receipt.image_url ? (
                  <div className="mb-3">
                    <img 
                      src={getImageUrl(receipt.image_url)}
                      alt="Receipt"
                      className="w-full h-24 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        setSelectedImageUrl(getImageUrl(receipt.image_url));
                        setShowImageModal(true);
                      }}
                      title="Click to view full size"
                      onError={(e) => {
                        console.log(`Failed to load receipt image: ${getImageUrl(receipt.image_url)}`);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="mb-3">
                    <div className="w-full h-24 bg-slate-100 rounded border flex items-center justify-center text-slate-400">
                      No image available
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900">
                        {receipt.seller || 'Unknown Seller'}
                      </h3>
                      <p className="text-sm text-slate-600">
                        GE: {geData ? `${geData.code} - ${geData.name}` : 'Unknown'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-green-600">
                        {receipt.currency} {receipt.amount || '0.00'}
                      </p>
                      {receipt.eur_amount && receipt.currency !== 'EUR' && (
                        <p className="text-sm text-blue-600">
                          EUR {receipt.eur_amount.toFixed(2)} (Rate: {receipt.exchange_rate?.toFixed(4)})
                        </p>
                      )}
                      <Badge variant="secondary">{receipt.category}</Badge>
                    </div>
                  </div>
                  
                  {receipt.description && (
                    <p className="text-sm text-slate-600">{receipt.description}</p>
                  )}
                  
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-xs text-slate-400">
                      {receipt.date ? new Date(receipt.date).toLocaleDateString() : 'No date'}
                    </span>
                    <div className="flex gap-1 flex-wrap">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openEditModal(receipt)}
                      >
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="secondary"
                        onClick={() => toggleReceiptStatus(receipt.id, receipt.status)}
                        className={receipt.status === 'handled' ? 
                          "bg-green-100 hover:bg-green-200 text-green-700" : 
                          "bg-blue-100 hover:bg-blue-200 text-blue-700"
                        }
                      >
                        {receipt.status === 'handled' ? '↺ Unhandled' : '✓ Handled'}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => deleteReceipt(receipt.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {receipts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500 mb-4">No receipts found</p>
          <Button onClick={openScanModal} variant="outline">
            Scan Your First Receipt
          </Button>
        </div>
      )}

      {/* Receipt Scan Modal */}
      <Dialog open={showScanModal} onOpenChange={setShowScanModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Scan Receipt</DialogTitle>
            <DialogDescription>
              Upload a receipt image for automatic data extraction
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* GE Selection */}
            <div>
              <Label htmlFor="ge-select">Select GE</Label>
              <Select value={selectedGE} onValueChange={setSelectedGE}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a GE..." />
                </SelectTrigger>
                <SelectContent>
                  {ges.map((ge) => (
                    <SelectItem key={ge.id} value={ge.id}>
                      {ge.code} - {ge.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Image Upload */}
            {!capturedImage && (
              <div>
                <Label htmlFor="receipt-upload">Receipt Image</Label>
                <Input
                  id="receipt-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageCapture}
                  disabled={isProcessing}
                />
              </div>
            )}

            {/* Processing Indicator */}
            {isProcessing && (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p>Processing receipt...</p>
                </div>
              </div>
            )}

            {/* Captured Image */}
            {capturedImage && !isProcessing && (
              <div>
                <Label>Scanned Receipt</Label>
                <img 
                  src={capturedImage} 
                  alt="Scanned receipt" 
                  className="w-full max-h-60 object-contain border rounded"
                />
                {extractedData && extractedData.confidence && (
                  <Badge variant="outline" className="mt-2">
                    {Math.round(extractedData.confidence * 100)}% confidence
                  </Badge>
                )}
              </div>
            )}

            {/* Form Fields */}
            {extractedData && !isProcessing && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="amount">Amount</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="currency">Currency</Label>
                  <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          {currency.code} - {currency.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="seller">Seller</Label>
                  <Input
                    id="seller"
                    value={formData.seller}
                    onChange={(e) => setFormData({...formData, seller: e.target.value})}
                    placeholder="Store or company name"
                  />
                </div>
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Meal">Meal</SelectItem>
                      <SelectItem value="Transport">Transport</SelectItem>
                      <SelectItem value="Hotel">Hotel</SelectItem>
                      <SelectItem value="Office">Office</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Brief description"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Input
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes"
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {extractedData && !isProcessing && (
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={saveReceipt}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={!selectedGE}
                >
                  Save Receipt
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setCapturedImage(null);
                    setExtractedData(null);
                  }}
                >
                  Scan Again
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Receipt Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Receipt</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-amount">Amount</Label>
              <Input
                id="edit-amount"
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit-currency">Currency</Label>
              <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-seller">Seller</Label>
              <Input
                id="edit-seller"
                value={formData.seller}
                onChange={(e) => setFormData({...formData, seller: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit-date">Date</Label>
              <Input
                id="edit-date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit-category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Meal">Meal</SelectItem>
                  <SelectItem value="Transport">Transport</SelectItem>
                  <SelectItem value="Hotel">Hotel</SelectItem>
                  <SelectItem value="Office">Office</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Input
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Input
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={updateReceipt} className="flex-1">
              Update Receipt
            </Button>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Enlargement Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Receipt Image</DialogTitle>
          </DialogHeader>
          {selectedImageUrl && (
            <img 
              src={selectedImageUrl} 
              alt="Receipt" 
              className="w-full max-h-[70vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

// Mileage Management Component
const MileageManagement = () => {
  const [mileageEntries, setMileageEntries] = useState([]);
  const [groupedEntries, setGroupedEntries] = useState({});
  const [ges, setGes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [showHandled, setShowHandled] = useState(false);
  const [filterGE, setFilterGE] = useState('all');
  
  // Mileage entry modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    ge_id: '',
    date: '',
    distance_km: '',
    purpose: '',
    notes: ''
  });

  useEffect(() => {
    fetchMileageEntries();
    fetchGEs();
  }, []);
  
  // Recalculate groupings when filter changes
  useEffect(() => {
    if (mileageEntries.length > 0) {
      // Recalculate groupings based on current filter
      fetchMileageEntries();
    }
  }, [showHandled]);

  const fetchMileageEntries = async () => {
    try {
      const [mileageRes, gesRes] = await Promise.all([
        axios.get(`${API}/mileage`),
        axios.get(`${API}/ges`)
      ]);
      
      const entries = mileageRes.data;
      const gesData = gesRes.data;
      
      setMileageEntries(entries);
      setGes(gesData);
      
      // Group entries by GE
      const gesMap = {};
      gesData.forEach(ge => {
        gesMap[ge.id] = ge;
      });
      
      const grouped = {};
      let totalDistance = 0;
      
      entries.forEach(entry => {
        // Apply filtering first
        if (!showHandled && entry.status === 'handled') {
          return; // Skip handled items if not showing them
        }
        
        const geId = entry.ge_id || 'no-ge';
        const geName = gesMap[geId] ? `${gesMap[geId].code} - ${gesMap[geId].name}` : 'No GE Assigned';
        
        if (!grouped[geId]) {
          grouped[geId] = {
            name: geName,
            entries: [],
            subtotal: 0,
            count: 0
          };
        }
        
        const distance = entry.distance_km || 0;
        grouped[geId].entries.push(entry);
        grouped[geId].subtotal += distance;
        grouped[geId].count += 1;
        totalDistance += distance;
      });
      
      // Add total to grouped data
      grouped._total = totalDistance;
      
      setGroupedEntries(grouped);
      
    } catch (error) {
      toast.error('Failed to load mileage entries');
    } finally {
      setLoading(false);
    }
  };

  const toggleMileageStatus = async (entryId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'handled' ? 'active' : 'handled';
      await axios.put(`${API}/mileage/${entryId}`, { status: newStatus });
      toast.success(`Mileage entry marked as ${newStatus}`);
      fetchMileageEntries();
    } catch (error) {
      toast.error('Failed to update mileage entry');
    }
  };

  const markAllMileageAsHandled = async () => {
    try {
      const unhandledEntries = mileageEntries.filter(e => e.status !== 'handled');
      const promises = unhandledEntries.map(entry => 
        axios.put(`${API}/mileage/${entry.id}`, { status: 'handled' })
      );
      await Promise.all(promises);
      toast.success(`Marked ${unhandledEntries.length} mileage entries as handled`);
      fetchMileageEntries();
    } catch (error) {
      toast.error('Failed to mark all mileage entries as handled');
    }
  };

  const fetchGEs = async () => {
    try {
      const response = await axios.get(`${API}/ges`);
      setGes(response.data);
    } catch (error) {
      console.error('Failed to load GEs:', error);
    }
  };

  const openAddModal = () => {
    setFormData({
      ge_id: '',
      date: new Date().toISOString().split('T')[0],
      distance_km: '',
      purpose: '',
      notes: ''
    });
    setShowAddModal(true);
  };

  const saveMileageEntry = async () => {
    if (!formData.ge_id || !formData.distance_km || !formData.purpose) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const entryData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        distance_km: parseFloat(formData.distance_km)
      };

      await axios.post(`${API}/mileage`, entryData);
      toast.success('Mileage entry saved successfully!');
      
      setShowAddModal(false);
      fetchMileageEntries();
    } catch (error) {
      toast.error('Failed to save mileage entry');
    }
  };

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setFormData({
      ge_id: entry.ge_id,
      date: entry.date ? entry.date.split('T')[0] : '',
      distance_km: entry.distance_km || '',
      purpose: entry.purpose || '',
      notes: entry.notes || ''
    });
    setShowEditModal(true);
  };

  const updateMileageEntry = async () => {
    try {
      const updateData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        distance_km: parseFloat(formData.distance_km)
      };

      await axios.put(`${API}/mileage/${editingEntry.id}`, updateData);
      toast.success('Mileage entry updated successfully!');
      
      setShowEditModal(false);
      setEditingEntry(null);
      fetchMileageEntries();
    } catch (error) {
      toast.error('Failed to update mileage entry');
    }
  };

  const deleteMileageEntry = async (entryId) => {
    if (!confirm('Are you sure you want to delete this mileage entry?')) return;

    try {
      await axios.delete(`${API}/mileage/${entryId}`);
      toast.success('Mileage entry deleted successfully');
      fetchMileageEntries();
    } catch (error) {
      toast.error('Failed to delete mileage entry');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8">Loading mileage entries...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-900">Mileage Register</h2>
        <Button onClick={openAddModal} className="bg-blue-600 hover:bg-blue-700">
          <span className="mr-2">🚗</span>
          Add Mileage
        </Button>
      </div>

      {/* Filter Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Checkbox 
                id="show-handled-mileage"
                checked={showHandled}
                onCheckedChange={setShowHandled}
              />
              <Label htmlFor="show-handled-mileage">Show handled items</Label>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="filter-ge-mileage">Filter by GE:</Label>
              <Select value={filterGE} onValueChange={setFilterGE}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All GEs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All GEs</SelectItem>
                  {ges.map(ge => (
                    <SelectItem key={ge.id} value={ge.id}>
                      {ge.code} - {ge.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="secondary"
                size="sm"
                onClick={markAllMileageAsHandled}
                className="bg-blue-100 hover:bg-blue-200 text-blue-700"
              >
                Mark All as Handled
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mileage Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Mileage Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {mileageEntries.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500 mb-4">No mileage entries found</p>
              <Button onClick={openAddModal} variant="outline">
                Add Your First Entry
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Mileage by GE */}
              {Object.entries(groupedEntries).filter(([key]) => {
                if (key === '_total') return false;
                if (filterGE && filterGE !== 'all' && key !== filterGE) return false;
                return true;
              }).map(([geId, geData]) => (
                <div key={geId} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4 bg-slate-50 p-3 rounded">
                    <h4 className="font-semibold text-lg">{geData.name}</h4>
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">
                        {geData.subtotal.toFixed(1)} km
                      </div>
                      <div className="text-sm text-slate-600">{geData.count} trips</div>
                    </div>
                  </div>
                  
                  {/* Entries for this GE */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Date</th>
                          <th className="text-right py-2 pr-8">Distance (km)</th>
                          <th className="text-left py-2 pl-8">Purpose</th>
                          <th className="text-center py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {geData.entries.filter(entry => {
                          // Filter by handled status
                          if (!showHandled && entry.status === 'handled') return false;
                          return true;
                        }).map((entry) => (
                          <tr key={entry.id} className="border-b hover:bg-slate-50">
                            <td className="py-2">
                              {entry.date ? new Date(entry.date).toLocaleDateString() : '-'}
                            </td>
                            <td className="py-2 text-right font-medium pr-8">
                              {entry.distance_km}
                            </td>
                            <td className="py-2 pl-8">
                              <div className="max-w-xs truncate" title={entry.purpose}>
                                {entry.purpose}
                              </div>
                            </td>
                            <td className="py-2 text-center">
                              <div className="flex gap-1 justify-center">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openEditModal(entry)}
                                >
                                  Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="secondary"
                                  onClick={() => toggleMileageStatus(entry.id, entry.status)}
                                  className={entry.status === 'handled' ? 
                                    "bg-green-100 hover:bg-green-200 text-green-700" : 
                                    "bg-blue-100 hover:bg-blue-200 text-blue-700"
                                  }
                                >
                                  {entry.status === 'handled' ? '↺ Unhandled' : '✓ Handled'}
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => deleteMileageEntry(entry.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              
              {/* Grand Total */}
              <div className="bg-slate-100 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <h4 className="font-bold text-xl">Total Distance</h4>
                  <div className="text-2xl font-bold text-blue-700">
                    {(groupedEntries._total || 0).toFixed(1)} km
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Mileage Entry Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Mileage Entry</DialogTitle>
            <DialogDescription>
              Log a new mileage entry for business travel
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ge-select">GE *</Label>
              <Select value={formData.ge_id} onValueChange={(value) => setFormData({...formData, ge_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a GE..." />
                </SelectTrigger>
                <SelectContent>
                  {ges.map((ge) => (
                    <SelectItem key={ge.id} value={ge.id}>
                      {ge.code} - {ge.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="distance">Distance (km) *</Label>
              <Input
                id="distance"
                type="number"
                step="0.1"
                value={formData.distance_km}
                onChange={(e) => setFormData({...formData, distance_km: e.target.value})}
                placeholder="0.0"
              />
            </div>
            <div>
              <Label htmlFor="purpose">Business Purpose *</Label>
              <Input
                id="purpose"
                value={formData.purpose}
                onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                placeholder="e.g., Client meeting, Site visit"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes (optional)"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={saveMileageEntry} className="flex-1">
              Save Entry
            </Button>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Mileage Entry Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Mileage Entry</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-ge-select">GE *</Label>
              <Select value={formData.ge_id} onValueChange={(value) => setFormData({...formData, ge_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a GE..." />
                </SelectTrigger>
                <SelectContent>
                  {ges.map((ge) => (
                    <SelectItem key={ge.id} value={ge.id}>
                      {ge.code} - {ge.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-date">Date *</Label>
              <Input
                id="edit-date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit-distance">Distance (km) *</Label>
              <Input
                id="edit-distance"
                type="number"
                step="0.1"
                value={formData.distance_km}
                onChange={(e) => setFormData({...formData, distance_km: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit-purpose">Business Purpose *</Label>
              <Input
                id="edit-purpose"
                value={formData.purpose}
                onChange={(e) => setFormData({...formData, purpose: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Input
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={updateMileageEntry} className="flex-1">
              Update Entry
            </Button>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// Settings Manager Component
const SettingsManager = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    max_file_size_mb: 20,
    ai_email_prompt: '',
    airport_analysis_prompt: ''
  });
  const [userSettings, setUserSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    max_file_size_mb: 20,
    ai_email_prompt: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [knackSyncing, setKnackSyncing] = useState(false);
  const [knackStatus, setKnackStatus] = useState(null);

  useEffect(() => {
    if (user && isAdmin(user)) {
      fetchSettings();
      fetchKnackStatus();
    }
    fetchUserSettings();
  }, [user]);

  const fetchKnackStatus = async () => {
    try {
      const response = await axios.get(`${API}/knack/sync-status`);
      setKnackStatus(response.data);
    } catch (error) {
      console.error('Failed to load Knack status:', error);
    }
  };

  const syncKnackProducts = async () => {
    setKnackSyncing(true);
    try {
      const response = await axios.post(`${API}/knack/sync-products`);
      toast.success(response.data.message);
      fetchKnackStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync products');
    } finally {
      setKnackSyncing(false);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSettings = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      const user = response.data;
      setUserSettings({
        smtp_host: user.smtp_host || '',
        smtp_port: user.smtp_port || 587,
        smtp_user: user.smtp_user || '',
        smtp_password: user.smtp_password || '',
        from_email: user.from_email || '',
        ai_email_prompt: user.ai_email_prompt || ''
      });
    } catch (error) {
      console.error('Failed to load user settings:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings`, settings);
      setSettings(response.data);
      toast.success('Global settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveUserSettings = async () => {
    setSavingUser(true);
    try {
      const response = await axios.get(`${API}/users/me`);
      const currentUser = response.data;
      
      const updatedUser = {
        ...currentUser,
        smtp_host: userSettings.smtp_host || null,
        smtp_port: userSettings.smtp_port || null,
        smtp_user: userSettings.smtp_user || null,
        smtp_password: userSettings.smtp_password || null,
        from_email: userSettings.from_email || null,
        ai_email_prompt: userSettings.ai_email_prompt || null
      };
      
      await axios.put(`${API}/users/${currentUser.id}`, updatedUser);
      toast.success('Your settings saved successfully');
    } catch (error) {
      toast.error('Failed to save your settings');
      console.error('Save user settings error:', error);
    } finally {
      setSavingUser(false);
    }
  };

  const testEmail = async () => {
    try {
      const response = await axios.post(`${API}/send-thank-you-email`, {
        to: settings.from_email || 'test@example.com',
        subject: 'Test Email from Velocity Airport Solutions',
        body: 'This is a test email to verify SMTP configuration.'
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to send test email');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Global Settings - Admin Only */}
      {user && isAdmin(user) && (
        <Card>
          <CardHeader>
            <CardTitle>Global Email Configuration</CardTitle>
            <CardDescription>
              Configure global SMTP settings (fallback when users don't have personal settings)
            </CardDescription>
          </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtp-host">SMTP Host</Label>
              <Input
                id="smtp-host"
                value={settings.smtp_host || ''}
                onChange={(e) => setSettings({...settings, smtp_host: e.target.value})}
                placeholder="smtp.gmail.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp-port">SMTP Port</Label>
              <Input
                id="smtp-port"
                type="number"
                value={settings.smtp_port || 587}
                onChange={(e) => setSettings({...settings, smtp_port: parseInt(e.target.value) || 587})}
                placeholder="587"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp-user">SMTP Username</Label>
              <Input
                id="smtp-user"
                value={settings.smtp_user || ''}
                onChange={(e) => setSettings({...settings, smtp_user: e.target.value})}
                placeholder="your-email@gmail.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp-password">SMTP Password</Label>
              <Input
                id="smtp-password"
                type="password"
                value={settings.smtp_password || ''}
                onChange={(e) => setSettings({...settings, smtp_password: e.target.value})}
                placeholder="App password or regular password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="from-email">From Email</Label>
              <Input
                id="from-email"
                type="email"
                value={settings.from_email || ''}
                onChange={(e) => setSettings({...settings, from_email: e.target.value})}
                placeholder="noreply@velocityairport.com"
              />
            </div>

            <div>
              <Label htmlFor="max-file-size">Max File Upload Size (MB)</Label>
              <Input
                id="max-file-size"
                type="number"
                value={settings.max_file_size_mb || 20}
                onChange={(e) => setSettings({...settings, max_file_size_mb: parseInt(e.target.value) || 20})}
                placeholder="20"
                min="1"
                max="100"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum file size for uploads in Media Center (1-100 MB)</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button variant="outline" onClick={testEmail} disabled={!settings.smtp_user || !settings.smtp_password}>
              Test Email
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Knack Product Database Sync - Admin Only */}
      {user && isAdmin(user) && (
      <Card>
        <CardHeader>
          <CardTitle>📦 Knack Product Database Sync</CardTitle>
          <CardDescription>
            Sync products from your Knack database to make them available to AI experts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {knackStatus && (
            <div className="bg-slate-50 p-4 rounded-md space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Last Sync:</span>
                <span className="text-sm text-slate-600">
                  {knackStatus.last_sync 
                    ? new Date(knackStatus.last_sync).toLocaleString()
                    : 'Never synced'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Products Synced:</span>
                <span className="text-sm text-slate-600">{knackStatus.products_count}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Status:</span>
                <span className={`text-sm font-semibold ${
                  knackStatus.status === 'synced' ? 'text-green-600' : 'text-slate-600'
                }`}>
                  {knackStatus.status === 'synced' ? '✓ Synced' : 'Not synced'}
                </span>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <p className="text-sm text-slate-600">
              <strong>What gets synced:</strong> Products marked as "Publish on Web" or "Agent portal" from your Knack database.
            </p>
            <p className="text-sm text-slate-600">
              <strong>Frequency:</strong> Automatic daily sync + manual trigger available here.
            </p>
          </div>

          <Button 
            onClick={syncKnackProducts}
            disabled={knackSyncing}
            className="w-full"
          >
            {knackSyncing ? 'Syncing Products...' : '🔄 Sync Products Now'}
          </Button>

          <p className="text-xs text-slate-500 mt-2">
            Note: Synced products appear in Knowledge Center with category "knack_products" and can be assigned to experts.
          </p>
        </CardContent>
      </Card>
      )}

      {/* User Email Settings - All Users */}
      <Card>
        <CardHeader>
          <CardTitle>My Email Settings</CardTitle>
          <CardDescription>
            Configure your personal SMTP settings. When configured, emails will be sent from your address.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="user-smtp-host">SMTP Host</Label>
              <Input
                id="user-smtp-host"
                value={userSettings.smtp_host || ''}
                onChange={(e) => setUserSettings({...userSettings, smtp_host: e.target.value})}
                placeholder="smtp.gmail.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-smtp-port">SMTP Port</Label>
              <Input
                id="user-smtp-port"
                type="number"
                value={userSettings.smtp_port || 587}
                onChange={(e) => setUserSettings({...userSettings, smtp_port: parseInt(e.target.value) || 587})}
                placeholder="587"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-smtp-user">SMTP Username</Label>
              <Input
                id="user-smtp-user"
                value={userSettings.smtp_user || ''}
                onChange={(e) => setUserSettings({...userSettings, smtp_user: e.target.value})}
                placeholder="your-email@gmail.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-smtp-password">SMTP Password</Label>
              <Input
                id="user-smtp-password"
                type="password"
                value={userSettings.smtp_password || ''}
                onChange={(e) => setUserSettings({...userSettings, smtp_password: e.target.value})}
                placeholder="App password or regular password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-from-email">From Email</Label>
              <Input
                id="user-from-email"
                type="email"
                value={userSettings.from_email || ''}
                onChange={(e) => setUserSettings({...userSettings, from_email: e.target.value})}
                placeholder="your-email@company.com"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={saveUserSettings} disabled={savingUser}>
              {savingUser ? 'Saving...' : 'Save My Email Settings'}
            </Button>
            <Button variant="outline" onClick={() => {
              setUserSettings({
                smtp_host: '',
                smtp_port: 587,
                smtp_user: '',
                smtp_password: '',
                from_email: '',
                max_file_size_mb: 20,
                ai_email_prompt: ''
              });
            }}>
              Clear My Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User AI Email Prompt - All Users */}
      <Card>
        <CardHeader>
          <CardTitle>My AI Email Instructions</CardTitle>
          <CardDescription>
            Customize how AI generates thank you emails for your contacts. Leave blank to use the default company template.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="user-ai-prompt">AI Email Instructions</Label>
            <textarea
              id="user-ai-prompt"
              className="w-full h-48 p-3 border border-gray-300 rounded-md resize-vertical font-mono text-sm"
              value={userSettings.ai_email_prompt || ''}
              onChange={(e) => setUserSettings({...userSettings, ai_email_prompt: e.target.value})}
              placeholder={`Leave blank to use default template, or customize with your own instructions...

Default template:
You are a professional email assistant for Velocity Airport Solutions. Generate personalized thank you emails for contacts met at aviation industry events.

Instructions:
- Use a warm, professional tone
- Reference the specific event and areas of interest discussed
- Highlight relevant Velocity solutions
- Include a clear call-to-action for follow-up
- Keep emails concise but engaging
- Always end with Velocity Airport Solutions branding

(See full default template in admin settings)`}
            />
          </div>
          <Button onClick={saveUserSettings} disabled={savingUser}>
            {savingUser ? 'Saving...' : 'Save AI Instructions'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>AI Email Configuration</CardTitle>
          <CardDescription>
            Configure the AI prompt for generating thank you emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ai-prompt">AI Email Prompt</Label>
            <textarea
              id="ai-prompt"
              className="w-full h-32 p-3 border border-gray-300 rounded-md resize-none"
              value={settings.ai_email_prompt || ''}
              onChange={(e) => setSettings({...settings, ai_email_prompt: e.target.value})}
              placeholder="Enter instructions for the AI to generate professional thank you emails..."
            />
          </div>
          <Button onClick={saveSettings} disabled={saving}>
            {saving ? 'Saving...' : 'Save AI Prompt'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>✈️ Airport Analysis Configuration</CardTitle>
          <CardDescription>
            Configure the AI prompt for analyzing airports in the Contact Finder
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="airport-prompt">Airport Analysis Prompt</Label>
            <textarea
              id="airport-prompt"
              className="w-full h-64 p-3 border border-gray-300 rounded-md resize-vertical font-mono text-sm"
              value={settings.airport_analysis_prompt || ''}
              onChange={(e) => setSettings({...settings, airport_analysis_prompt: e.target.value})}
              placeholder="Enter instructions for AI to analyze airports (runway configuration, personnel contacts, operational details, etc.)"
            />
            <p className="text-xs text-muted-foreground">
              This prompt guides the AI in analyzing airport infrastructure, identifying key personnel contacts, and providing relevant operational information for marketing purposes.
            </p>
          </div>
          <Button onClick={saveSettings} disabled={saving}>
            {saving ? 'Saving...' : 'Save Airport Analysis Prompt'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Reports Dashboard Component
const ReportsDashboard = () => {
  const [ges, setGes] = useState([]);
  const [selectedGE, setSelectedGE] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [reports, setReports] = useState({
    expenses: null,
    mileage: null,
    cards: null,
    summary: null
  });
  const [loading, setLoading] = useState({
    expenses: false,
    mileage: false,
    cards: false,
    summary: false
  });
  const [activeReport, setActiveReport] = useState(null); // Track which report is currently showing

  useEffect(() => {
    fetchGEs();
    fetchSummaryReport();
  }, []);

  const fetchGEs = async () => {
    try {
      const response = await axios.get(`${API}/ges`);
      setGes(response.data);
    } catch (error) {
      console.error('Failed to load GEs:', error);
    }
  };

  const fetchExpenseReport = async () => {
    setLoading(prev => ({...prev, expenses: true}));
    try {
      const params = new URLSearchParams();
      if (selectedGE && selectedGE !== 'all') params.append('ge_id', selectedGE);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      
      const response = await axios.get(`${API}/reports/expenses?${params.toString()}`);
      
      // Fetch GE data for grouping
      const gesResponse = await axios.get(`${API}/ges`);
      const gesMap = {};
      gesResponse.data.forEach(ge => {
        gesMap[ge.id] = ge;
      });
      
      // Group receipts by GE
      const receiptsByGE = {};
      const currencyTotals = {};
      let grandTotal = 0;
      
      response.data.receipts.forEach(receipt => {
        const geId = receipt.ge_id || 'no-ge';
        const geName = gesMap[geId] ? `${gesMap[geId].code} - ${gesMap[geId].name}` : 'No GE Assigned';
        
        if (!receiptsByGE[geId]) {
          receiptsByGE[geId] = {
            name: geName,
            receipts: [],
            subtotal: 0,
            count: 0,
            currencies: {}
          };
        }
        
        const currency = receipt.currency || 'USD';
        const originalAmount = receipt.amount || 0;
        
        // Calculate EUR amount if not available (for existing receipts)
        let eurAmount = receipt.eur_amount;
        if (!eurAmount && originalAmount && currency) {
          // Use simple conversion rates for existing data
          const conversionRates = {
            'EUR': 1.0,
            'USD': 0.85,
            'GBP': 1.15,
            'DKK': 0.134,
            'SEK': 0.087,
            'NOK': 0.086,
            'CHF': 0.92,
            'CAD': 0.62,
            'AUD': 0.56,
            'JPY': 0.0058
          };
          const rate = conversionRates[currency] || 1.0;
          eurAmount = originalAmount * rate;
        }
        eurAmount = eurAmount || originalAmount || 0;
        
        receiptsByGE[geId].receipts.push(receipt);
        receiptsByGE[geId].subtotal += eurAmount; // Use EUR for subtotals
        receiptsByGE[geId].count += 1;
        
        // Track original currencies per GE (for display purposes)
        if (!receiptsByGE[geId].currencies[currency]) {
          receiptsByGE[geId].currencies[currency] = { amount: 0, count: 0 };
        }
        receiptsByGE[geId].currencies[currency].amount += originalAmount;
        receiptsByGE[geId].currencies[currency].count += 1;
        
        // Overall currency totals (original currencies for reference)
        if (!currencyTotals[currency]) {
          currencyTotals[currency] = { amount: 0, count: 0 };
        }
        currencyTotals[currency].amount += originalAmount;
        currencyTotals[currency].count += 1;
        
        grandTotal += eurAmount; // Use EUR for grand total
      });
      
      // Add enhanced data with GE grouping
      const enhancedData = {
        ...response.data,
        receipts_by_ge: receiptsByGE,
        currency_summary: currencyTotals,
        grand_total: grandTotal
      };
      
      setReports(prev => ({...prev, expenses: enhancedData}));
      setActiveReport('expenses');
    } catch (error) {
      toast.error('Failed to load expense report');
    } finally {
      setLoading(prev => ({...prev, expenses: false}));
    }
  };

  const fetchMileageReport = async () => {
    setLoading(prev => ({...prev, mileage: true}));
    try {
      const params = new URLSearchParams();
      if (selectedGE && selectedGE !== 'all') params.append('ge_id', selectedGE);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      
      const response = await axios.get(`${API}/reports/mileage?${params.toString()}`);
      
      // Fetch GE data for grouping
      const gesResponse = await axios.get(`${API}/ges`);
      const gesMap = {};
      gesResponse.data.forEach(ge => {
        gesMap[ge.id] = ge;
      });
      
      // Group mileage entries by GE
      const mileageByGE = {};
      let grandTotalDistance = 0;
      
      response.data.mileage_entries.forEach(entry => {
        const geId = entry.ge_id || 'no-ge';
        const geName = gesMap[geId] ? `${gesMap[geId].code} - ${gesMap[geId].name}` : 'No GE Assigned';
        
        if (!mileageByGE[geId]) {
          mileageByGE[geId] = {
            name: geName,
            entries: [],
            subtotal_distance: 0,
            count: 0
          };
        }
        
        const distance = entry.distance_km || 0;
        
        mileageByGE[geId].entries.push(entry);
        mileageByGE[geId].subtotal_distance += distance;
        mileageByGE[geId].count += 1;
        
        grandTotalDistance += distance;
      });
      
      // Add enhanced data with GE grouping
      const enhancedData = {
        ...response.data,
        mileage_by_ge: mileageByGE,
        grand_total_distance: grandTotalDistance
      };
      
      setReports(prev => ({...prev, mileage: enhancedData}));
      setActiveReport('mileage');
    } catch (error) {
      toast.error('Failed to load mileage report');
    } finally {
      setLoading(prev => ({...prev, mileage: false}));
    }
  };

  const fetchCardsReport = async () => {
    setLoading(prev => ({...prev, cards: true}));
    try {
      const params = new URLSearchParams();
      if (selectedGE && selectedGE !== 'all') params.append('ge_id', selectedGE);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      
      const [cardsResponse, eventsResponse] = await Promise.all([
        axios.get(`${API}/cards?${params.toString()}`),
        axios.get(`${API}/events`)
      ]);
      
      const cardsData = cardsResponse.data;
      const eventsData = eventsResponse.data;
      
      // Create events map for quick lookup
      const eventsMap = {};
      eventsData.forEach(event => {
        eventsMap[event.id] = event;
      });
      
      // Add event names to cards
      const cardsWithEventNames = cardsData.map(card => ({
        ...card,
        event_name: card.event_id && eventsMap[card.event_id] ? eventsMap[card.event_id].name : null
      }));
      
      const processedReport = {
        cards: cardsWithEventNames,
        events_map: eventsMap,
        summary: {
          total_cards: cardsData.length,
          companies: [...new Set(cardsData.map(card => card.company).filter(Boolean))].length,
          events: [...new Set(cardsData.map(card => card.event_id).filter(Boolean))].length
        }
      };
      
      setReports(prev => ({...prev, cards: processedReport}));
      setActiveReport('cards');
    } catch (error) {
      toast.error('Failed to load business cards report');
    } finally {
      setLoading(prev => ({...prev, cards: false}));
    }
  };

  const fetchSummaryReport = async () => {
    setLoading(prev => ({...prev, summary: true}));
    try {
      const params = (selectedGE && selectedGE !== 'all') ? `?ge_id=${selectedGE}` : '';
      const response = await axios.get(`${API}/reports/summary${params}`);
      setReports(prev => ({...prev, summary: response.data}));
    } catch (error) {
      toast.error('Failed to load summary report');
    } finally {
      setLoading(prev => ({...prev, summary: false}));
    }
  };

  // Export Functions
  const exportToPDF = async (reportType) => {
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;
      
      // Get current user info
      const userResponse = await axios.get(`${API}/users/me`);
      const currentUser = userResponse.data;
      
      // Get the report container
      const reportElement = document.getElementById(`${reportType}-report-content`);
      if (!reportElement) {
        toast.error('Report content not found');
        return;
      }
      
      // Wait a bit for images to load
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        height: reportElement.scrollHeight,
        windowHeight: reportElement.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Add proper margins (20mm on all sides)
      const margin = 20;
      const imgWidth = 210 - (margin * 2); // A4 width minus margins
      const pageHeight = 297 - (margin * 2); // A4 height minus margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add header with user info
      pdf.setFontSize(16);
      pdf.text('Velocity Airport Solutions', margin, margin - 5);
      
      pdf.setFontSize(10);
      pdf.text(`Generated by: ${currentUser.name} (${currentUser.email})`, margin, margin + 5);
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, margin, margin + 10);
      pdf.text(`Report Type: ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`, margin, margin + 15);
      
      // Add line separator
      pdf.line(margin, margin + 18, 210 - margin, margin + 18);
      
      let position = margin + 25; // Start after header
      let heightLeft = imgHeight;
      
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - 25); // Account for header space
      
      while (heightLeft > 0) {
        pdf.addPage();
        position = margin;
        
        // Add header to new pages
        pdf.setFontSize(10);
        pdf.text('Velocity Airport Solutions - Continued', margin, position - 5);
        pdf.line(margin, position - 2, 210 - margin, position - 2);
        
        position = position + 5;
        const remainingHeight = Math.min(heightLeft, pageHeight - 10);
        const yOffset = imgHeight - heightLeft;
        
        // Create a new canvas with just the remaining content
        const remainingCanvas = document.createElement('canvas');
        const ctx = remainingCanvas.getContext('2d');
        remainingCanvas.width = canvas.width;
        remainingCanvas.height = (remainingHeight * canvas.width) / imgWidth;
        
        ctx.drawImage(canvas, 0, (yOffset * canvas.width) / imgWidth, canvas.width, remainingCanvas.height, 0, 0, canvas.width, remainingCanvas.height);
        
        const remainingImgData = remainingCanvas.toDataURL('image/png');
        pdf.addImage(remainingImgData, 'PNG', margin, position, imgWidth, remainingHeight);
        
        heightLeft -= (pageHeight - 10);
      }
      
      pdf.save(`${reportType}-report-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      // Hide receipt images section after PDF generation
      if (reportType === 'expenses') {
        const receiptImagesSection = document.getElementById('receipt-images-section');
        if (receiptImagesSection) {
          receiptImagesSection.classList.add('hidden');
          receiptImagesSection.classList.remove('block');
        }
      }
    }
  };

  // Print function removed per user request

  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', reportType: '' });
  
  // Thank you email states
  const [showThankYouDialog, setShowThankYouDialog] = useState(false);
  const [thankYouEmailData, setThankYouEmailData] = useState({
    recipient_email: '',
    recipient_name: '',
    company: '',
    event_name: '',
    interests: [],
    subject: '',
    body: ''
  });
  const [generatingEmail, setGeneratingEmail] = useState(false);

  const openEmailDialog = (reportType) => {
    setEmailData({ email: '', reportType });
    setShowEmailDialog(true);
  };

  const sendReport = async () => {
    if (!emailData.email) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      // First generate PDF data
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;
      
      const reportElement = document.getElementById(`${emailData.reportType}-report-content`);
      if (!reportElement) {
        toast.error('Report content not found');
        return;
      }

      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Get PDF as blob
      const pdfBlob = pdf.output('blob');

      // Create form data for email
      const formData = new FormData();
      formData.append('email', emailData.email);
      formData.append('subject', `${emailData.reportType.charAt(0).toUpperCase() + emailData.reportType.slice(1)} Report - ${new Date().toLocaleDateString()}`);
      formData.append('body', `Please find the ${emailData.reportType} report attached.\n\nGenerated on: ${new Date().toLocaleString()}`);
      formData.append('report', pdfBlob, `${emailData.reportType}-report.pdf`);

      // Send email via backend
      await axios.post(`${API}/send-report-email`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`Report sent successfully to ${emailData.email}`);
      setShowEmailDialog(false);
    } catch (error) {
      console.error('Email send failed:', error);
      toast.error('Failed to send report. Using mailto as fallback.');
      
      // Fallback to mailto
      const subject = `${emailData.reportType.charAt(0).toUpperCase() + emailData.reportType.slice(1)} Report - ${new Date().toLocaleDateString()}`;
      const body = `Please find the ${emailData.reportType} report attached.\n\nGenerated on: ${new Date().toLocaleString()}`;
      const mailtoLink = `mailto:${emailData.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoLink);
      
      setShowEmailDialog(false);
    }
  };

  const openThankYouEmailDialog = async (card) => {
    // Pre-populate email data
    const eventName = card.event_name || 'our event';
    const recipientName = card.name || 'valued contact';
    const company = card.company || 'your organization';
    const interests = card.interests || [];
    
    setThankYouEmailData({
      recipient_email: card.email || '',
      recipient_name: recipientName,
      company: company,
      event_name: eventName,
      interests: interests,
      subject: `Thank you for visiting ${eventName} - Velocity Airport Solutions`,
      body: 'Generating personalized email...'
    });
    
    setShowThankYouDialog(true);
    setGeneratingEmail(true);
    
    try {
      // Generate AI-powered email
      const emailResponse = await axios.post(`${API}/generate-thank-you-email`, {
        recipient_name: recipientName,
        company: company,
        event_name: eventName,
        interests: interests
      });
      
      setThankYouEmailData(prev => ({
        ...prev,
        subject: emailResponse.data.subject,
        body: emailResponse.data.body
      }));
      
    } catch (error) {
      console.error('Failed to generate email:', error);
      
      // Fallback to template email
      const fallbackBody = `Dear ${recipientName},

Thank you for visiting our booth at ${eventName}. It was a pleasure meeting you and learning about ${company}.

${interests.length > 0 ? 
  `Based on your interest in ${interests.join(', ')}, I believe Velocity Airport Solutions can provide valuable expertise in these areas. We specialize in comprehensive airport solutions and would be happy to discuss how we can support ${company}'s needs.` : 
  'Velocity Airport Solutions specializes in comprehensive airport solutions and we would be happy to discuss how we can support your organization.'
}

We would welcome the opportunity to continue our conversation and explore potential collaboration opportunities.

Please feel free to reach out at your convenience.

Best regards,
Velocity Airport Solutions Team`;
      
      setThankYouEmailData(prev => ({
        ...prev,
        body: fallbackBody
      }));
    } finally {
      setGeneratingEmail(false);
    }
  };

  const sendThankYouEmail = async () => {
    if (!thankYouEmailData.recipient_email) {
      toast.error('Please enter a recipient email address');
      return;
    }

    try {
      await axios.post(`${API}/send-thank-you-email`, {
        to: thankYouEmailData.recipient_email,
        subject: thankYouEmailData.subject,
        body: thankYouEmailData.body,
        card_id: selectedCard?.id // Include card ID to auto-mark email as sent
      });

      toast.success(`Thank you email sent to ${thankYouEmailData.recipient_email}`);
      setShowThankYouDialog(false);
      
      // Refresh cards to show updated email_sent status
      fetchCards();
    } catch (error) {
      console.error('Failed to send thank you email:', error);
      toast.error('Failed to send email. Opening in email client...');
      
      // Fallback to mailto
      const mailtoLink = `mailto:${thankYouEmailData.recipient_email}?subject=${encodeURIComponent(thankYouEmailData.subject)}&body=${encodeURIComponent(thankYouEmailData.body)}`;
      window.open(mailtoLink);
      
      setShowThankYouDialog(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-900">Reports & Analytics</h2>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Report Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="report-ge-select">Filter by GE</Label>
              <Select value={selectedGE} onValueChange={setSelectedGE}>
                <SelectTrigger>
                  <SelectValue placeholder="All GEs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All GEs</SelectItem>
                  {ges.map((ge) => (
                    <SelectItem key={ge.id} value={ge.id}>
                      {ge.code} - {ge.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={fetchExpenseReport} className="flex-1" disabled={loading.expenses}>
              {loading.expenses ? 'Loading...' : 'Expense Report'}
            </Button>
            <Button onClick={fetchMileageReport} className="flex-1" disabled={loading.mileage}>
              {loading.mileage ? 'Loading...' : 'Mileage Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {reports.summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Business Cards</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {reports.summary.business_cards}
              </div>
              <p className="text-sm text-slate-600">Total contacts</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Expenses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                EUR {reports.summary.receipts.total_amount?.toFixed(2) || '0.00'}
              </div>
              <p className="text-sm text-slate-600">
                {reports.summary.receipts.count} receipts
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Mileage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {reports.summary.mileage.total_distance_km} km
              </div>
              <p className="text-sm text-slate-600">
                {reports.summary.mileage.count} trips
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expense Report */}
      {reports.expenses && activeReport === 'expenses' && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Expense Report</CardTitle>
                <CardDescription>
                  Grand Total: ${reports.expenses.grand_total?.toFixed(2) || '0.00'} across {reports.expenses.summary.total_receipts} receipts
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => exportToPDF('expenses')}>
                  📄 PDF
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEmailDialog('expenses')}>
                  📧 Email
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent id="expenses-report-content">
            {/* Currency Summary (Original Currencies for Reference) */}
            <div className="mb-6">
              <h4 className="font-semibold mb-3">Currency Breakdown (Original Amounts)</h4>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {Object.entries(reports.expenses.currency_summary || {}).map(([currency, data]) => (
                  <div key={currency} className="text-center p-3 bg-blue-50 rounded">
                    <div className="font-semibold">{currency}</div>
                    <div className="text-sm text-slate-600">{data.count} receipts</div>
                    <div className="font-bold text-blue-600">{currency} {data.amount.toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                * All totals below are calculated in EUR using exchange rates from the receipt dates
              </p>
            </div>
            
            {/* Expenses by GE */}
            <div className="space-y-6">
              {Object.entries(reports.expenses.receipts_by_ge || {}).map(([geId, geData]) => (
                <div key={geId} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4 bg-slate-50 p-3 rounded">
                    <h4 className="font-semibold text-lg">{geData.name}</h4>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">
                        EUR {geData.subtotal.toFixed(2)}
                      </div>
                      <div className="text-sm text-slate-600">{geData.count} receipts</div>
                    </div>
                  </div>
                  
                  {/* Currency breakdown per GE */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(geData.currencies).map(([currency, currData]) => (
                        <Badge key={currency} variant="outline" className="bg-green-50">
                          {currency}: {currData.amount.toFixed(2)} ({currData.count} receipts)
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Receipts for this GE */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Date</th>
                          <th className="text-left py-2">Seller</th>
                          <th className="text-left py-2">Category</th>
                          <th className="text-right py-2">Original Amount</th>
                          <th className="text-right py-2">EUR Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {geData.receipts.map((receipt) => (
                          <tr key={receipt.id} className="border-b hover:bg-slate-50">
                            <td className="py-2">
                              {receipt.date ? new Date(receipt.date).toLocaleDateString() : '-'}
                            </td>
                            <td className="py-2">{receipt.seller || 'Unknown'}</td>
                            <td className="py-2">
                              <Badge variant="secondary">{receipt.category}</Badge>
                            </td>
                            <td className="py-2 text-right font-medium">
                              {receipt.currency} {receipt.amount?.toFixed(2) || '0.00'}
                            </td>
                            <td className="py-2 text-right font-medium text-green-600">
                              EUR {receipt.eur_amount?.toFixed(2) || '0.00'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              
              {/* Grand Total */}
              <div className="bg-slate-100 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <h4 className="font-bold text-xl">Grand Total</h4>
                  <div className="text-2xl font-bold text-green-700">
                    EUR {reports.expenses.grand_total?.toFixed(2) || '0.00'}
                  </div>
                </div>
              </div>
              
              {/* Receipt Images Section - Visible in both HTML and PDF */}
              <div id="receipt-images-section" className="mt-8 space-y-6">
                <div className="border-t-2 pt-6">
                  <h3 className="text-xl font-bold mb-6 text-center">Receipt Images for Validation</h3>
                  {Object.entries(reports.expenses.receipts_by_ge || {}).map(([geId, geData]) => (
                    <div key={geId} className="mb-8">
                      <h4 className="font-semibold text-lg mb-4 bg-slate-100 p-2 rounded">{geData.name}</h4>
                      <div className="grid gap-6">
                        {geData.receipts.filter(receipt => receipt.image_url).map((receipt, index) => (
                          <div key={receipt.id} className="break-inside-avoid">
                            <div className="border rounded-lg p-4 bg-white">
                              <div className="text-sm font-medium mb-2">
                                Receipt #{index + 1}: {receipt.seller || 'Unknown'} - {receipt.currency} {receipt.amount?.toFixed(2) || '0.00'}
                              </div>
                              <div className="text-xs text-slate-600 mb-3">
                                Date: {receipt.date ? new Date(receipt.date).toLocaleDateString() : 'Unknown'} | 
                                Category: {receipt.category} | 
                                EUR Amount: {receipt.eur_amount?.toFixed(2) || '0.00'}
                              </div>
                              <div className="text-center">
                                <img 
                                  src={getImageUrl(receipt.image_url)}
                                  alt={`Receipt from ${receipt.seller}`}
                                  className="max-w-full h-auto border border-gray-300 rounded"
                                  style={{ maxHeight: '400px', margin: '0 auto' }}
                                  crossOrigin="anonymous"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'block';
                                  }}
                                />
                                <div style={{ display: 'none' }} className="text-center p-8 bg-gray-100 rounded text-gray-500">
                                  Receipt image not available
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mileage Report */}
      {reports.mileage && activeReport === 'mileage' && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Mileage Report</CardTitle>
                <CardDescription>
                  Grand Total: {reports.mileage.grand_total_distance?.toFixed(1) || '0.0'} km across {reports.mileage.summary.total_entries} trips
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => exportToPDF('mileage')}>
                  📄 PDF
                </Button>
                {/* Print button removed per user request */}
                <Button size="sm" variant="outline" onClick={() => openEmailDialog('mileage')}>
                  📧 Email
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent id="mileage-report-content">
            {/* Mileage by GE */}
            <div className="space-y-6">
              {Object.entries(reports.mileage.mileage_by_ge || {}).map(([geId, geData]) => (
                <div key={geId} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4 bg-slate-50 p-3 rounded">
                    <h4 className="font-semibold text-lg">{geData.name}</h4>
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">
                        {geData.subtotal_distance.toFixed(1)} km
                      </div>
                      <div className="text-sm text-slate-600">{geData.count} trips</div>
                    </div>
                  </div>
                  
                  {/* Mileage entries for this GE */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Date</th>
                          <th className="text-left py-2 pl-8">Purpose</th>
                          <th className="text-right py-2 pr-8">Distance (km)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {geData.entries.map((entry) => (
                          <tr key={entry.id} className="border-b hover:bg-slate-50">
                            <td className="py-2">
                              {entry.date ? new Date(entry.date).toLocaleDateString() : '-'}
                            </td>
                            <td className="py-2 pl-8">{entry.purpose}</td>
                            <td className="py-2 text-right font-medium pr-8">{entry.distance_km}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              
              {/* Grand Total */}
              <div className="bg-slate-100 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <h4 className="font-bold text-xl">Grand Total Distance</h4>
                  <div className="text-2xl font-bold text-blue-700">
                    {reports.mileage.grand_total_distance?.toFixed(1) || '0.0'} km
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Business Cards Report removed - moved to Cards section */}

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Email Report</DialogTitle>
            <DialogDescription>
              Send the {emailData.reportType} report to an email address
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="email-address">Email Address</Label>
              <Input
                id="email-address"
                type="email"
                value={emailData.email}
                onChange={(e) => setEmailData({...emailData, email: e.target.value})}
                placeholder="recipient@example.com"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={sendReport} className="flex-1">
              Send Report
            </Button>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Thank You Email Dialog */}
      <Dialog open={showThankYouDialog} onOpenChange={setShowThankYouDialog}>
        <DialogContent className="max-w-4xl h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Compose Email</DialogTitle>
            <DialogDescription>
              AI-generated personalized email for {thankYouEmailData.recipient_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="thank-you-email">Recipient Email</Label>
                <Input
                  id="thank-you-email"
                  type="email"
                  value={thankYouEmailData.recipient_email}
                  onChange={(e) => setThankYouEmailData({...thankYouEmailData, recipient_email: e.target.value})}
                  placeholder="recipient@example.com"
                />
              </div>
              <div>
                <Label htmlFor="thank-you-subject">Subject</Label>
                <Input
                  id="thank-you-subject"
                  value={thankYouEmailData.subject}
                  onChange={(e) => setThankYouEmailData({...thankYouEmailData, subject: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="thank-you-body">Email Body</Label>
              {generatingEmail ? (
                <div className="flex items-center justify-center p-8 border rounded">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p>Generating personalized email with AI...</p>
                  </div>
                </div>
              ) : (
                <textarea
                  id="thank-you-body"
                  value={thankYouEmailData.body}
                  onChange={(e) => setThankYouEmailData({...thankYouEmailData, body: e.target.value})}
                  className="w-full h-80 p-3 border rounded-md resize-vertical"
                  placeholder="Email body will be generated automatically..."
                />
              )}
            </div>
            
            <div className="text-sm text-slate-600 bg-blue-50 p-3 rounded">
              <strong>Context:</strong> Event: {thankYouEmailData.event_name} | 
              Company: {thankYouEmailData.company} | 
              Interests: {thankYouEmailData.interests.length > 0 ? thankYouEmailData.interests.join(', ') : 'None specified'}
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={sendThankYouEmail} className="flex-1" disabled={generatingEmail || !thankYouEmailData.recipient_email}>
              Send Email
            </Button>
            <Button variant="outline" onClick={() => setShowThankYouDialog(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    users: 0,
    cards: 0,
    receipts: 0,
    mileage: 0,
    agents: 0,
    tasks: {
      total: 0,
      open: 0,
      completed: 0,
      overdue: [],
      due_soon: []
    }
  });

  const isAdmin = (user) => {
    return user?.role_id && user.role_id !== '' && 
      (user.role_name === 'admin' || user.role_type === 'admin');
  };

  useEffect(() => {
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      console.log('Fetching stats for user:', user?.email);
      
      const requests = [
        axios.get(`${API}/cards`),
        axios.get(`${API}/receipts`),
        axios.get(`${API}/mileage`),
        axios.get(`${API}/tasks/stats`)
      ];

      // Add admin-specific requests
      if (isAdmin(user)) {
        requests.push(
          axios.get(`${API}/users`),
          axios.get(`${API}/agents`)
        );
      }

      const responses = await Promise.all(requests);
      
      console.log('Cards response:', responses[0].data);
      console.log('Receipts response:', responses[1].data);
      console.log('Mileage response:', responses[2].data);
      console.log('Tasks response:', responses[3].data);
      
      const newStats = {
        cards: Array.isArray(responses[0].data) ? responses[0].data.length : 0,
        receipts: Array.isArray(responses[1].data) ? responses[1].data.length : 0,
        mileage: Array.isArray(responses[2].data) ? responses[2].data.length : 0,
        tasks: responses[3].data || {
          total: 0,
          open: 0,
          completed: 0,
          overdue: [],
          due_soon: []
        }
      };

      if (isAdmin(user)) {
        console.log('Users response:', responses[4].data);
        console.log('Agents response:', responses[5].data);
        newStats.users = Array.isArray(responses[4].data) ? responses[4].data.length : 0;
        newStats.agents = Array.isArray(responses[5].data) ? responses[5].data.length : 0;
      }

      console.log('Final stats:', newStats);
      setStats(newStats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Dashboard</h2>
        <p className="text-slate-600">Welcome back, {user?.name}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Access your most used features</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Button 
            variant="outline" 
            className="h-24 flex flex-col space-y-2" 
            onClick={() => {
              document.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'cards' }));
            }}
          >
            <div className="text-lg">📱</div>
            <span>Scan Business Card</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-24 flex flex-col space-y-2"
            onClick={() => {
              document.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'receipts' }));
            }}
          >
            <div className="text-lg">🧾</div>
            <span>Add Receipt</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-24 flex flex-col space-y-2"
            onClick={() => {
              document.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'mileage' }));
            }}
          >
            <div className="text-lg">🚗</div>
            <span>Log Mileage</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-24 flex flex-col space-y-2"
            onClick={() => {
              document.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'tasks' }));
            }}
          >
            <div className="text-lg">✅</div>
            <span>Manage Tasks</span>
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {isAdmin(user) && (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.users}</div>
                <p className="text-xs text-muted-foreground">System users</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Agents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.agents}</div>
                <p className="text-xs text-muted-foreground">Active agents</p>
              </CardContent>
            </Card>
          </>
        )}
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Business Cards</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.cards}</div>
            <p className="text-xs text-muted-foreground">Scanned cards</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receipts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.receipts}</div>
            <p className="text-xs text-muted-foreground">Processed receipts</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mileage Records</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.mileage}</div>
            <p className="text-xs text-muted-foreground">Total entries</p>
          </CardContent>
        </Card>
      </div>

      {/* Task Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Tasks Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Tasks</span>
                <span className="font-semibold">{stats.tasks.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Open</span>
                <span className="font-semibold text-blue-600">{stats.tasks.open}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Completed</span>
                <span className="font-semibold text-green-600">{stats.tasks.completed}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-red-600">Overdue Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.tasks.overdue && stats.tasks.overdue.length > 0 ? (
              <div className="space-y-2">
                {stats.tasks.overdue.slice(0, 3).map((task, idx) => (
                  <div key={idx} className="text-sm">
                    <p className="font-medium truncate">{task.title}</p>
                    <p className="text-xs text-red-600">{task.days_overdue} days overdue</p>
                  </div>
                ))}
                {stats.tasks.overdue.length > 3 && (
                  <p className="text-xs text-gray-500">+{stats.tasks.overdue.length - 3} more</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No overdue tasks 🎉</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-orange-600">Due Soon</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.tasks.due_soon && stats.tasks.due_soon.length > 0 ? (
              <div className="space-y-2">
                {stats.tasks.due_soon.slice(0, 3).map((task, idx) => (
                  <div key={idx} className="text-sm">
                    <p className="font-medium truncate">{task.title}</p>
                    <p className="text-xs text-orange-600">Due in {task.days_until} days</p>
                  </div>
                ))}
                {stats.tasks.due_soon.length > 3 && (
                  <p className="text-xs text-gray-500">+{stats.tasks.due_soon.length - 3} more</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No tasks due soon</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main App Component
const MainApp = () => {
  const { user } = useAuth(); // Get user from auth context
  const [activeTab, setActiveTab] = useState('dashboard');

  // Marketing & Expert Center State
  const [knowledgeDocs, setKnowledgeDocs] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [socialInput, setSocialInput] = useState('');
  const [socialGenerating, setSocialGenerating] = useState(false);
  const [socialResults, setSocialResults] = useState(null);
  const [socialImage, setSocialImage] = useState(null);
  const [emailResponderInput, setEmailResponderInput] = useState('');
  const [emailResponderInstruction, setEmailResponderInstruction] = useState('');
  const [emailResponderResponse, setEmailResponderResponse] = useState('');
  const [emailResponding, setEmailResponding] = useState(false);
  const [experts, setExperts] = useState([]);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [expertMessage, setExpertMessage] = useState('');
  const [expertConversation, setExpertConversation] = useState(null);
  const [expertChatting, setExpertChatting] = useState(false);
  const [showCreateExpert, setShowCreateExpert] = useState(false);
  const [uploadingKnowledge, setUploadingKnowledge] = useState(false);
  const [marketingSubTab, setMarketingSubTab] = useState('social');
  const [editingExpert, setEditingExpert] = useState(null);
  const [previewingDoc, setPreviewingDoc] = useState(null);
  const [docFilter, setDocFilter] = useState('');
  
  // Email Marketing states
  const [emailLists, setEmailLists] = useState([]);
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [showCreateList, setShowCreateList] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignContent, setCampaignContent] = useState('');
  const [campaignEditorContent, setCampaignEditorContent] = useState('');
  const [editCampaignContent, setEditCampaignContent] = useState('');
  const [addingSubscriberToList, setAddingSubscriberToList] = useState(null);
  const [viewingList, setViewingList] = useState(null);
  const [editingCampaignData, setEditingCampaignData] = useState(null);
  const createQuillRef = useRef(null);
  const editQuillRef = useRef(null);
  
  // Airport Contact Finder states
  const [airportSearchType, setAirportSearchType] = useState('country'); // country, icao, name
  const [selectedContinent, setSelectedContinent] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [icaoSearch, setIcaoSearch] = useState('');
  const [airportNameSearch, setAirportNameSearch] = useState('');
  const [countriesByContinent, setCountriesByContinent] = useState({});
  const [airportResults, setAirportResults] = useState([]);
  const [searchingAirports, setSearchingAirports] = useState(false);
  
  // Custom image handlers defined with useCallback to maintain stable references
  const createImageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          
          const response = await axios.post(`${API}/upload-image`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          const quill = createQuillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', response.data.url);
            quill.setSelection(range.index + 1);
          }
          
          toast.success('Image uploaded successfully');
        } catch (error) {
          console.error('Image upload error:', error);
          toast.error('Failed to upload image');
        }
      }
    };
  }, []);

  const editImageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          
          const response = await axios.post(`${API}/upload-image`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          const quill = editQuillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', response.data.url);
            quill.setSelection(range.index + 1);
          }
          
          toast.success('Image uploaded successfully');
        } catch (error) {
          console.error('Image upload error:', error);
          toast.error('Failed to upload image');
        }
      }
    };
  }, []);
  
  // Memoized Quill modules configuration for Create Campaign editor
  const createEditorModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: createImageHandler
      }
    },
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize', 'Toolbar']
    },
    clipboard: {
      matchVisual: false
    }
  }), [createImageHandler]);

  // Memoized Quill modules configuration for Edit Campaign editor
  const editEditorModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: editImageHandler
      }
    },
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize', 'Toolbar']
    },
    clipboard: {
      matchVisual: false
    }
  }), [editImageHandler]);

  // Memoized formats array
  const quillFormats = useMemo(() => [
    'header', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link', 'image',
    'width', 'height', 'float'
  ], []);
  
  // Expert chat ref for auto-scroll
  const chatContainerRef = useRef(null);

  useEffect(() => {
    const handleTabNavigation = (event) => {
      setActiveTab(event.detail);
    };

    document.addEventListener('navigate-to-tab', handleTabNavigation);
    return () => document.removeEventListener('navigate-to-tab', handleTabNavigation);
  }, []);

  // Fetch marketing data when tab is active
  useEffect(() => {
    if (activeTab === 'marketing' && user && isAdmin(user)) {
      fetchKnowledgeDocs();
      fetchSocialPosts();
      fetchEmailLists();
      fetchEmailCampaigns();
    }
  }, [activeTab]);

  // Fetch experts when tab is active
  useEffect(() => {
    if (activeTab === 'experts') {
      fetchExperts();
      fetchKnowledgeDocs(); // Fetch knowledge docs for expert assignment
    }
  }, [activeTab]);

  const fetchKnowledgeDocs = async () => {
    try {
      const response = await axios.get(`${API}/knowledge`);
      setKnowledgeDocs(response.data);
    } catch (error) {
      console.error('Failed to fetch knowledge docs:', error);
    }
  };

  const fetchSocialPosts = async () => {
    try {
      const response = await axios.get(`${API}/social-media/posts`);
      setSocialPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch social posts:', error);
    }
  };

  // Fetch email lists on component mount for admins (for cards dropdown)
  useEffect(() => {
    if (user && isAdmin(user)) {
      fetchEmailLists();
    }
  }, [user]);


  const fetchExperts = async () => {
    try {
      const response = await axios.get(`${API}/experts`);
      setExperts(response.data);
    } catch (error) {
      console.error('Failed to fetch experts:', error);
    }
  };

  const uploadKnowledgeDoc = async (file, title, content, category, tags) => {
    setUploadingKnowledge(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      if (content) formData.append('content', content);
      if (category) formData.append('category', category);
      if (tags) formData.append('tags', tags);
      if (file) formData.append('file', file);

      await axios.post(`${API}/knowledge`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Knowledge document uploaded successfully');
      fetchKnowledgeDocs();
    } catch (error) {
      toast.error('Failed to upload document');
      console.error(error);
    } finally {
      setUploadingKnowledge(false);
    }
  };

  const deleteKnowledgeDoc = async (docId) => {
    if (!window.confirm('Delete this knowledge document?')) return;
    try {
      await axios.delete(`${API}/knowledge/${docId}`);
      toast.success('Document deleted');
      fetchKnowledgeDocs();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Auto-scroll chat when messages change
  useEffect(() => {
    if (chatContainerRef.current && expertConversation) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [expertConversation]);

  // Format message content (simple markdown-like formatting)
  const formatMessage = (content) => {
    if (!content) return '';
    
    // Split by line breaks
    const lines = content.split('\n');
    return lines.map((line, idx) => {
      // Bold text (**text**)
      line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Bullet points
      if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
        return `<li key="${idx}">${line.trim().substring(2)}</li>`;
      }
      // Numbers
      if (line.trim().match(/^\d+\./)) {
        return `<li key="${idx}">${line.trim().replace(/^\d+\./, '')}</li>`;
      }
      return `<div key="${idx}">${line || '<br/>'}</div>`;
    }).join('');
  };

  // Email Marketing Functions
  const fetchEmailLists = async () => {
    try {
      const response = await axios.get(`${API}/email-lists`);
      setEmailLists(response.data);
    } catch (error) {
      console.error('Failed to fetch email lists:', error);
    }
  };

  const fetchEmailCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/email-campaigns`);
      setEmailCampaigns(response.data);
    } catch (error) {
      console.error('Failed to fetch email campaigns:', error);
    }
  };

  const createEmailList = async (listData) => {
    try {
      const formData = new FormData();
      formData.append('name', listData.name);
      if (listData.description) formData.append('description', listData.description);

      await axios.post(`${API}/email-lists`, formData);
      toast.success('Email list created');
      setShowCreateList(false);
      fetchEmailLists();
    } catch (error) {
      toast.error('Failed to create list');
    }
  };

  const addSubscribers = async (listId, file, singleEmail) => {
    try {
      const formData = new FormData();
      if (file) {
        formData.append('subscribers_csv', file);
      } else if (singleEmail) {
        formData.append('email', singleEmail.email);
        formData.append('first_name', singleEmail.first_name || '');
        formData.append('last_name', singleEmail.last_name || '');
      }

      const response = await axios.post(`${API}/email-lists/${listId}/subscribers`, formData);
      toast.success(`Added ${response.data.added} subscribers`);
      fetchEmailLists();
    } catch (error) {
      toast.error('Failed to add subscribers');
    }
  };

  // Airport Contact Finder functions
  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/marketing/airports/countries`);
      setCountriesByContinent(response.data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      toast.error('Failed to load countries');
    }
  };

  const searchAirports = async () => {
    setSearchingAirports(true);
    setAirportResults([]);
    
    try {
      const formData = new FormData();
      
      // Priority: ICAO > Name > Country
      if (icaoSearch.trim()) {
        formData.append('icao_code', icaoSearch.trim());
      } else if (airportNameSearch.trim()) {
        formData.append('airport_name', airportNameSearch.trim());
      } else if (selectedCountry) {
        formData.append('country_code', selectedCountry);
      } else {
        toast.error('Please select a country, enter ICAO code, or airport name');
        setSearchingAirports(false);
        return;
      }
      
      const response = await axios.post(`${API}/marketing/airports/search`, formData);
      setAirportResults(response.data);
      
      if (response.data.length === 0) {
        toast.info('No airports found matching your search');
      }
    } catch (error) {
      console.error('Failed to search airports:', error);
      toast.error('Failed to search airports');
    } finally {
      setSearchingAirports(false);
    }
  };

  const deleteEmailList = async (listId) => {
    if (!window.confirm('Delete this email list and all subscribers?')) return;
    try {
      await axios.delete(`${API}/email-lists/${listId}`);
      toast.success('Email list deleted');
      fetchEmailLists();
    } catch (error) {
      toast.error('Failed to delete list');
    }
  };


  const removeSubscriber = async (listId, email) => {
    if (!window.confirm(`Remove ${email} from this list?`)) return;
    try {
      await axios.delete(`${API}/email-lists/${listId}/subscribers/${encodeURIComponent(email)}`);
      toast.success('Subscriber removed');
      fetchEmailLists();
      // Update viewing list if it's currently open
      if (viewingList && viewingList.id === listId) {
        const updatedList = await axios.get(`${API}/email-lists/${listId}`);
        setViewingList(updatedList.data);
      }
    } catch (error) {
      toast.error('Failed to remove subscriber');
    }
  };


  const createEmailCampaign = async (campaignData) => {
    try {
      // Use the HTML content directly from react-quill
      const htmlContent = campaignEditorContent;
      
      const formData = new FormData();
      formData.append('title', campaignData.title);
      formData.append('subject', campaignData.subject);
      formData.append('content', htmlContent);
      formData.append('list_ids', campaignData.list_ids.join(','));
      
      if (campaignData.title_image) {
        formData.append('title_image', campaignData.title_image);
      }

      await axios.post(`${API}/email-campaigns`, formData);
      toast.success('Campaign created');
      setShowCreateCampaign(false);
      setCampaignEditorContent('');
      fetchEmailCampaigns();
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  const sendCampaign = async (campaignId, intervalMinutes) => {
    if (!window.confirm('Send this campaign now? This action cannot be undone.')) return;
    try {
      const formData = new FormData();
      formData.append('send_interval_minutes', intervalMinutes || 5);
      
      const response = await axios.post(`${API}/email-campaigns/${campaignId}/send`, formData);
      toast.success(`Campaign sent to ${response.data.sent} recipients`);
      fetchEmailCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    }
  };

  const updateEmailCampaign = async (campaignId, campaignData) => {
    try {
      const htmlContent = editCampaignContent;
      
      const formData = new FormData();
      formData.append('title', campaignData.title);
      formData.append('subject', campaignData.subject);
      formData.append('content', htmlContent);
      formData.append('list_ids', campaignData.list_ids.join(','));

      await axios.put(`${API}/email-campaigns/${campaignId}`, formData);
      toast.success('Campaign updated');
      setEditingCampaignData(null);
      setEditCampaignContent('');
      fetchEmailCampaigns();
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const scheduleCampaign = async (campaignId, scheduledTime, intervalMinutes) => {
    try {
      const formData = new FormData();
      formData.append('scheduled_time', scheduledTime);
      formData.append('send_interval_minutes', intervalMinutes || 5);
      
      await axios.post(`${API}/email-campaigns/${campaignId}/schedule`, formData);
      toast.success('Campaign scheduled');
      fetchEmailCampaigns();
    } catch (error) {
      toast.error('Failed to schedule campaign');
    }
  };

  const deleteEmailCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await axios.delete(`${API}/email-campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      fetchEmailCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };


  const generateSocialContent = async () => {
    if (!socialInput.trim()) {
      toast.error('Please enter content topic');
      return;
    }

    setSocialGenerating(true);
    setSocialResults(null);

    try {
      const platforms = ['linkedin', 'facebook', 'x', 'website'];
      const results = {};

      for (const platform of platforms) {
        const formData = new FormData();
        formData.append('input_text', socialInput);
        formData.append('platform', platform);
        if (socialImage) formData.append('image', socialImage);

        const response = await axios.post(`${API}/social-media/generate`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        results[platform] = {
          content: response.data.generated_content,
          image_url: response.data.image_url
        };
      }

      setSocialResults(results);
      toast.success('Content generated for all platforms!');
      fetchSocialPosts();
    } catch (error) {
      toast.error('Failed to generate content');
      console.error(error);
    } finally {
      setSocialGenerating(false);
    }
  };

  const generateEmailResponse = async () => {
    if (!emailResponderInput.trim()) {
      toast.error('Please paste email content');
      return;
    }

    setEmailResponding(true);
    try {
      const formData = new FormData();
      formData.append('email_content', emailResponderInput);
      if (emailResponderInstruction) {
        formData.append('additional_instruction', emailResponderInstruction);
      }

      const response = await axios.post(`${API}/email-responder`, formData);
      setEmailResponderResponse(response.data.response);
    } catch (error) {
      toast.error('Failed to generate response');
      console.error(error);
    } finally {
      setEmailResponding(false);
    }
  };

  const chatWithExpert = async () => {
    if (!expertMessage.trim() || !selectedExpert) return;

    setExpertChatting(true);
    try {
      const formData = new FormData();
      formData.append('message', expertMessage);
      if (expertConversation) {
        formData.append('conversation_id', expertConversation.id);
      }

      const response = await axios.post(
        `${API}/experts/${selectedExpert.id}/chat`,
        formData
      );

      setExpertConversation({
        id: response.data.conversation_id,
        messages: [
          ...(expertConversation?.messages || []),
          { role: 'user', content: expertMessage, timestamp: new Date().toISOString() },
          { role: 'assistant', content: response.data.response, timestamp: new Date().toISOString() }
        ]
      });
      setExpertMessage('');
    } catch (error) {
      toast.error('Failed to get expert response');
      console.error(error);
    } finally {
      setExpertChatting(false);
    }
  };

  const createExpert = async (expertData) => {
    try {
      const formData = new FormData();
      Object.keys(expertData).forEach(key => {
        formData.append(key, expertData[key]);
      });

      await axios.post(`${API}/experts`, formData);
      toast.success('Expert created successfully');
      fetchExperts();
      setShowCreateExpert(false);
    } catch (error) {
      toast.error('Failed to create expert');
      console.error(error);
    }
  };

  const updateExpert = async (expertId, expertData) => {
    try {
      const formData = new FormData();
      Object.keys(expertData).forEach(key => {
        formData.append(key, expertData[key]);
      });

      await axios.put(`${API}/experts/${expertId}`, formData);
      toast.success('Expert updated successfully');
      fetchExperts();
      setEditingExpert(null);
    } catch (error) {
      toast.error('Failed to update expert');
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar Navigation */}
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      
      {/* Main Content Area */}
      <div className="flex-1 ml-64">
        <div className="px-8 py-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          
          <TabsContent value="dashboard" data-testid="dashboard-content">
            <Dashboard />
          </TabsContent>
          
          <TabsContent value="users" data-testid="users-content">
            <div className="space-y-8">
              <UserManagement />
              <Separator />
              <EventManagement />
            </div>
          </TabsContent>
          
          <TabsContent value="ges" data-testid="ges-content">
            <GEManagement />
          </TabsContent>
          
          <TabsContent value="cards" data-testid="cards-content">
            <div className="space-y-8">
              <BusinessCardScanner />
              {user && isAdmin(user) && (
                <>
                  <Separator />
                  <EventManagement />
                </>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="receipts" data-testid="receipts-content">
            <ReceiptManagement />
          </TabsContent>
          
          <TabsContent value="mileage" data-testid="mileage-content">
            <MileageManagement />
          </TabsContent>
          
          <TabsContent value="reports" data-testid="reports-content">
            <ReportsDashboard />
          </TabsContent>

          {user && isAdmin(user) && (
            <TabsContent value="marketing" data-testid="marketing-content">
              <div className="space-y-6">
                {/* Marketing Sub-Navigation */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex gap-2 overflow-x-auto">
                      <Button
                        variant={marketingSubTab === 'social' ? 'default' : 'outline'}
                        onClick={() => setMarketingSubTab('social')}
                        size="sm"
                      >
                        📱 Social Media
                      </Button>
                      <Button
                        variant={marketingSubTab === 'responder' ? 'default' : 'outline'}
                        onClick={() => setMarketingSubTab('responder')}
                        size="sm"
                      >
                        ✉️ Email Responder
                      </Button>
                      <Button
                        variant={marketingSubTab === 'campaigns' ? 'default' : 'outline'}
                        onClick={() => setMarketingSubTab('campaigns')}
                        size="sm"
                      >
                        📧 Email Marketing
                      </Button>
                      <Button
                        variant={marketingSubTab === 'airports' ? 'default' : 'outline'}
                        onClick={() => setMarketingSubTab('airports')}
                        size="sm"
                      >
                        ✈️ Airport Contact Finder
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Social Media Generator */}
                {marketingSubTab === 'social' && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Social Media Content Generator</CardTitle>
                      <CardDescription>
                        Generate AI-powered content for all platforms at once
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="social-input">What do you want to talk about?</Label>
                        <textarea
                          id="social-input"
                          className="w-full h-32 p-3 border rounded-md mt-2"
                          placeholder="Enter your topic, event, product announcement, etc... AI will generate platform-specific content for LinkedIn, Facebook, X, and Website."
                          value={socialInput}
                          onChange={(e) => setSocialInput(e.target.value)}
                        />
                      </div>

                      <div>
                        <Label htmlFor="social-image">Optional Image</Label>
                        <input
                          id="social-image"
                          type="file"
                          accept="image/*"
                          className="block w-full mt-2"
                          onChange={(e) => setSocialImage(e.target.files[0])}
                        />
                      </div>

                      <Button
                        onClick={generateSocialContent}
                        disabled={socialGenerating || !socialInput.trim()}
                        className="w-full"
                      >
                        {socialGenerating ? 'Generating...' : '🚀 Generate Content for All Platforms'}
                      </Button>

                      {socialResults && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                          {/* LinkedIn */}
                          <Card>
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg">LinkedIn</CardTitle>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(socialResults.linkedin.content);
                                    toast.success('Copied to clipboard!');
                                  }}
                                >
                                  📋 Copy
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="bg-slate-50 p-3 rounded text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                                {socialResults.linkedin.content}
                              </div>
                            </CardContent>
                          </Card>

                          {/* Facebook */}
                          <Card>
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg">Facebook</CardTitle>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(socialResults.facebook.content);
                                    toast.success('Copied to clipboard!');
                                  }}
                                >
                                  📋 Copy
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="bg-slate-50 p-3 rounded text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                                {socialResults.facebook.content}
                              </div>
                            </CardContent>
                          </Card>

                          {/* X (Twitter) */}
                          <Card>
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg">X (Twitter)</CardTitle>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(socialResults.x.content);
                                    toast.success('Copied to clipboard!');
                                  }}
                                >
                                  📋 Copy
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="bg-slate-50 p-3 rounded text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                                {socialResults.x.content}
                              </div>
                            </CardContent>
                          </Card>

                          {/* Website News */}
                          <Card>
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg">Website News</CardTitle>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(socialResults.website.content);
                                    toast.success('Copied to clipboard!');
                                  }}
                                >
                                  📋 Copy
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="bg-slate-50 p-3 rounded text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                                {socialResults.website.content}
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Email Responder */}
                {marketingSubTab === 'responder' && (
                  <Card>
                    <CardHeader>
                      <CardTitle>AI Email Responder</CardTitle>
                      <CardDescription>
                        Paste an email and get an AI-generated professional response
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="email-input">Paste Email Content</Label>
                        <textarea
                          id="email-input"
                          className="w-full h-48 p-3 border rounded-md mt-2 font-mono text-sm"
                          placeholder="Paste the email you received here..."
                          value={emailResponderInput}
                          onChange={(e) => setEmailResponderInput(e.target.value)}
                        />
                      </div>

                      <Button
                        onClick={generateEmailResponse}
                        disabled={emailResponding || !emailResponderInput.trim()}
                        className="w-full"
                      >
                        {emailResponding ? 'Generating Response...' : '✨ Generate Professional Response'}
                      </Button>

                      {emailResponderResponse && (
                        <div className="space-y-4 mt-6">
                          <div className="flex items-center justify-between">
                            <Label>AI-Generated Response</Label>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(emailResponderResponse);
                                toast.success('Response copied to clipboard!');
                              }}
                            >
                              📋 Copy Response
                            </Button>
                          </div>
                          <div className="bg-slate-50 p-4 rounded border whitespace-pre-wrap font-mono text-sm max-h-96 overflow-y-auto">
                            {emailResponderResponse}
                          </div>

                          <div>
                            <Label htmlFor="refine-instruction">Refine Response (Optional)</Label>
                            <input
                              id="refine-instruction"
                              type="text"
                              className="w-full p-2 border rounded-md mt-2"
                              placeholder="e.g., Make it more aggressive, Mention NextGen AGL system, Be more friendly"
                              value={emailResponderInstruction}
                              onChange={(e) => setEmailResponderInstruction(e.target.value)}
                            />
                          </div>

                          {emailResponderInstruction && (
                            <Button
                              onClick={generateEmailResponse}
                              disabled={emailResponding}
                              variant="outline"
                              className="w-full"
                            >
                              {emailResponding ? 'Refining...' : '🔄 Regenerate with Instructions'}
                            </Button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Email Marketing - Full Implementation */}
                {marketingSubTab === 'campaigns' && (
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-2xl font-bold">{emailLists.length}</div>
                          <div className="text-sm text-slate-500">Email Lists</div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-2xl font-bold">{emailCampaigns.length}</div>
                          <div className="text-sm text-slate-500">Campaigns</div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-2xl font-bold">
                            {emailCampaigns.reduce((sum, c) => sum + (c.sent_count || 0), 0)}
                          </div>
                          <div className="text-sm text-slate-500">Total Sent</div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-2xl font-bold">
                            {emailCampaigns.length > 0 
                              ? Math.round((emailCampaigns.reduce((sum, c) => sum + (c.open_count || 0), 0) / 
                                  Math.max(emailCampaigns.reduce((sum, c) => sum + (c.sent_count || 0), 0), 1)) * 100)
                              : 0}%
                          </div>
                          <div className="text-sm text-slate-500">Avg. Open Rate</div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Email Lists Section */}
                    <Card>
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <div>
                            <CardTitle>📋 Email Lists</CardTitle>
                            <CardDescription>Manage your subscriber lists</CardDescription>
                          </div>
                          <Button size="sm" onClick={() => setShowCreateList(true)}>
                            ➕ New List
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {emailLists.length === 0 ? (
                          <div className="text-center py-8 text-slate-500">
                            No email lists yet. Create your first list to get started.
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {emailLists.map(list => (
                              <div key={list.id} className="border rounded-lg p-4 flex justify-between items-center">
                                <div>
                                  <h4 className="font-semibold">{list.name}</h4>
                                  <p className="text-sm text-slate-500">{list.description || 'No description'}</p>
                                  <p className="text-xs text-slate-400 mt-1">
                                    {list.subscribers?.length || 0} subscribers
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => setViewingList(list)}
                                  >
                                    👁️ View ({list.subscribers?.length || 0})
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => setAddingSubscriberToList(list.id)}
                                  >
                                    ➕ Add Person
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      const input = document.createElement('input');
                                      input.type = 'file';
                                      input.accept = '.csv';
                                      input.onchange = (e) => addSubscribers(list.id, e.target.files[0], null);
                                      input.click();
                                    }}
                                  >
                                    📤 Import CSV
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => deleteEmailList(list.id)}
                                  >
                                    🗑️
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Create List Modal */}
                    {showCreateList && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <Card className="max-w-md w-full">
                          <CardHeader>
                            <CardTitle>Create Email List</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div>
                                <Label>List Name</Label>
                                <input
                                  id="list-name"
                                  className="w-full p-2 border rounded-md mt-1"
                                  placeholder="e.g., Newsletter Subscribers"
                                />
                              </div>
                              <div>
                                <Label>Description (optional)</Label>
                                <textarea
                                  id="list-description"
                                  className="w-full p-2 border rounded-md mt-1"
                                  rows="3"
                                  placeholder="Brief description of this list"
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  className="flex-1"
                                  onClick={() => {
                                    const name = document.getElementById('list-name').value;
                                    const description = document.getElementById('list-description').value;
                                    if (!name) {
                                      toast.error('Please enter a list name');
                                      return;
                                    }
                                    createEmailList({ name, description });
                                  }}
                                >
                                  Create List
                                </Button>
                                <Button variant="outline" onClick={() => setShowCreateList(false)}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}

                    {/* Add Subscriber Modal */}
                    {addingSubscriberToList && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <Card className="max-w-md w-full">
                          <CardHeader>
                            <CardTitle>Add Subscriber</CardTitle>
                            <CardDescription>
                              Add an individual person to the email list
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div>
                                <Label>Email Address *</Label>
                                <input
                                  id="subscriber-email"
                                  type="email"
                                  className="w-full p-2 border rounded-md mt-1"
                                  placeholder="john@example.com"
                                  required
                                />
                              </div>
                              <div>
                                <Label>First Name</Label>
                                <input
                                  id="subscriber-firstname"
                                  type="text"
                                  className="w-full p-2 border rounded-md mt-1"
                                  placeholder="John"
                                />
                              </div>
                              <div>
                                <Label>Last Name</Label>
                                <input
                                  id="subscriber-lastname"
                                  type="text"
                                  className="w-full p-2 border rounded-md mt-1"
                                  placeholder="Doe"
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  className="flex-1"
                                  onClick={() => {
                                    const email = document.getElementById('subscriber-email').value;
                                    const firstName = document.getElementById('subscriber-firstname').value;
                                    const lastName = document.getElementById('subscriber-lastname').value;
                                    
                                    if (!email) {
                                      toast.error('Please enter an email address');
                                      return;
                                    }
                                    
                                    addSubscribers(addingSubscriberToList, null, {
                                      email,
                                      first_name: firstName,
                                      last_name: lastName
                                    });
                                    setAddingSubscriberToList(null);
                                  }}
                                >
                                  Add Subscriber
                                </Button>
                                <Button variant="outline" onClick={() => setAddingSubscriberToList(null)}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}


                    {/* View List Modal */}
                    {viewingList && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <Card className="max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                          <CardHeader>
                            <div className="flex justify-between items-center">
                              <div>
                                <CardTitle>{viewingList.name}</CardTitle>
                                <CardDescription>{viewingList.subscribers?.length || 0} subscribers</CardDescription>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setViewingList(null)}
                              >
                                ✕
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="overflow-y-auto">
                            {(!viewingList.subscribers || viewingList.subscribers.length === 0) ? (
                              <p className="text-center text-slate-500 py-8">
                                No subscribers yet. Add some to get started.
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {viewingList.subscribers.map((sub, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-3 border rounded hover:bg-slate-50">
                                    <div>
                                      <div className="font-medium">
                                        {sub.first_name && sub.last_name 
                                          ? `${sub.first_name} ${sub.last_name}` 
                                          : sub.first_name || sub.last_name || 'No name'}
                                      </div>
                                      <div className="text-sm text-slate-600">{sub.email}</div>
                                      <div className="text-xs text-slate-400">
                                        Added: {new Date(sub.subscribed_at).toLocaleDateString()}
                                      </div>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => removeSubscriber(viewingList.id, sub.email)}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      🗑️ Remove
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    )}


                    {/* Campaigns Section */}
                    <Card>
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <div>
                            <CardTitle>📧 Email Campaigns</CardTitle>
                            <CardDescription>Create and manage email campaigns</CardDescription>
                          </div>
                          <Button 
                            size="sm" 
                            onClick={() => {
                              if (emailLists.length === 0) {
                                toast.error('Create an email list first');
                                return;
                              }
                              setShowCreateCampaign(true);
                            }}
                          >
                            ➕ New Campaign
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {emailCampaigns.length === 0 ? (
                          <div className="text-center py-8 text-slate-500">
                            No campaigns yet. Create your first campaign to start sending emails.
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {emailCampaigns.map(campaign => (
                              <div key={campaign.id} className="border rounded-lg p-4">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <h4 className="font-semibold">{campaign.title}</h4>
                                    <p className="text-sm text-slate-500">{campaign.subject}</p>
                                  </div>
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    campaign.status === 'sent' ? 'bg-green-100 text-green-700' :
                                    campaign.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`}>
                                    {campaign.status}
                                  </span>
                                </div>
                                <div className="flex gap-4 text-xs text-slate-500 mb-3">
                                  <span>📤 {campaign.sent_count || 0} sent</span>
                                  <span>📖 {campaign.open_count || 0} opened</span>
                                  <span>🔗 {campaign.click_count || 0} clicked</span>
                                </div>
                                {campaign.status !== 'sent' && (
                                  <div className="flex gap-2">
                                    <Button 
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        // Load campaign HTML content for editing
                                        setEditCampaignContent(campaign.content || '');
                                        setEditingCampaignData(campaign);
                                      }}
                                    >
                                      ✏️ Edit
                                    </Button>
                                    {campaign.status === 'draft' && (
                                      <Button 
                                        size="sm"
                                        onClick={() => sendCampaign(campaign.id, 5)}
                                      >
                                        📧 Send Now
                                      </Button>
                                    )}
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => deleteEmailCampaign(campaign.id)}
                                    >
                                      🗑️ Delete
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Create Campaign Modal - Basic Version */}
                    {showCreateCampaign && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                        <Card className="max-w-3xl w-full my-8">
                          <CardHeader>
                            <CardTitle>Create Email Campaign</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div>
                                <Label>Campaign Title</Label>
                                <input
                                  id="campaign-title"
                                  className="w-full p-2 border rounded-md mt-1"
                                  placeholder="e.g., Monthly Newsletter - January"
                                />
                              </div>
                              <div>
                                <Label>Email Subject Line</Label>
                                <input
                                  id="campaign-subject"
                                  className="w-full p-2 border rounded-md mt-1"
                                  placeholder="What recipients will see in their inbox"
                                />
                              </div>
                              <div>
                                <Label>Select Email Lists</Label>
                                <select
                                  id="campaign-lists"
                                  multiple
                                  className="w-full p-2 border rounded-md mt-1"
                                  size="3"
                                >
                                  {emailLists.map(list => (
                                    <option key={list.id} value={list.id}>
                                      {list.name} ({list.subscribers?.length || 0} subscribers)
                                    </option>
                                  ))}
                                </select>
                                <p className="text-xs text-slate-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
                              </div>
                              <div>
                                <Label>Email Content</Label>
                                <div className="mt-1">
                                  <ReactQuill
                                    key="create-campaign-editor"
                                    ref={createQuillRef}
                                    theme="snow"
                                    value={campaignEditorContent}
                                    onChange={(content, delta, source, editor) => {
                                      setCampaignEditorContent(content);
                                    }}
                                    style={{ height: '400px', marginBottom: '50px' }}
                                    bounds=".ql-editor"
                                    modules={createEditorModules}
                                    formats={quillFormats}
                                  />
                                </div>
                                <div className="mt-2 flex gap-2 flex-wrap">
                                  <p className="text-xs text-slate-600 w-full mb-1">Click to insert placeholders:</p>
                                  <button
                                    type="button"
                                    className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded cursor-pointer"
                                    onClick={() => {
                                      const quill = createQuillRef.current?.getEditor();
                                      if (quill) {
                                        const range = quill.getSelection(true);
                                        quill.insertText(range.index, '{first_name}');
                                        quill.setSelection(range.index + 12);
                                      }
                                    }}
                                  >
                                    {'{first_name}'}
                                  </button>
                                  <button
                                    type="button"
                                    className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded cursor-pointer"
                                    onClick={() => {
                                      const quill = createQuillRef.current?.getEditor();
                                      if (quill) {
                                        const range = quill.getSelection(true);
                                        quill.insertText(range.index, '{last_name}');
                                        quill.setSelection(range.index + 11);
                                      }
                                    }}
                                  >
                                    {'{last_name}'}
                                  </button>
                                  <button
                                    type="button"
                                    className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded cursor-pointer"
                                    onClick={() => {
                                      const quill = createQuillRef.current?.getEditor();
                                      if (quill) {
                                        const range = quill.getSelection(true);
                                        quill.insertText(range.index, '{email}');
                                        quill.setSelection(range.index + 7);
                                      }
                                    }}
                                  >
                                    {'{email}'}
                                  </button>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  className="flex-1"
                                  onClick={() => {
                                    const title = document.getElementById('campaign-title').value;
                                    const subject = document.getElementById('campaign-subject').value;
                                    const listSelect = document.getElementById('campaign-lists');
                                    const selectedLists = Array.from(listSelect.selectedOptions).map(opt => opt.value);
                                    
                                    if (!title || !subject || selectedLists.length === 0) {
                                      toast.error('Please fill in all required fields and select at least one list');
                                      return;
                                    }
                                    
                                    createEmailCampaign({ title, subject, list_ids: selectedLists });
                                  }}
                                >
                                  Create Campaign
                                </Button>
                                <Button variant="outline" onClick={() => {
                                  setShowCreateCampaign(false);
                                  setCampaignContent('');
                                }}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}


                    {/* Edit Campaign Modal */}
                    {editingCampaignData && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                        <Card className="max-w-3xl w-full my-8">
                          <CardHeader>
                            <CardTitle>Edit Campaign: {editingCampaignData.title}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div>
                                <Label>Campaign Title</Label>
                                <input
                                  id="edit-campaign-title"
                                  className="w-full p-2 border rounded-md mt-1"
                                  defaultValue={editingCampaignData.title}
                                />
                              </div>
                              <div>
                                <Label>Email Subject Line</Label>
                                <input
                                  id="edit-campaign-subject"
                                  className="w-full p-2 border rounded-md mt-1"
                                  defaultValue={editingCampaignData.subject}
                                />
                              </div>
                              <div>
                                <Label>Select Email Lists</Label>
                                <select
                                  id="edit-campaign-lists"
                                  multiple
                                  className="w-full p-2 border rounded-md mt-1"
                                  size="3"
                                  defaultValue={editingCampaignData.list_ids}
                                >
                                  {emailLists.map(list => (
                                    <option key={list.id} value={list.id}>
                                      {list.name} ({list.subscribers?.length || 0} subscribers)
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <Label>Email Content</Label>
                                <div className="mt-1">
                                  <ReactQuill
                                    key={`edit-campaign-editor-${editingCampaignData?.id}`}
                                    ref={editQuillRef}
                                    theme="snow"
                                    value={editCampaignContent}
                                    onChange={(content, delta, source, editor) => {
                                      setEditCampaignContent(content);
                                    }}
                                    style={{ height: '400px', marginBottom: '50px' }}
                                    bounds=".ql-editor"
                                    modules={editEditorModules}
                                    formats={quillFormats}
                                  />
                                </div>
                                <div className="mt-2 flex gap-2 flex-wrap">
                                  <p className="text-xs text-slate-600 w-full mb-1">Click to insert placeholders:</p>
                                  <button
                                    type="button"
                                    className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded cursor-pointer"
                                    onClick={() => {
                                      const quill = editQuillRef.current?.getEditor();
                                      if (quill) {
                                        const range = quill.getSelection(true);
                                        quill.insertText(range.index, '{first_name}');
                                        quill.setSelection(range.index + 12);
                                      }
                                    }}
                                  >
                                    {'{first_name}'}
                                  </button>
                                  <button
                                    type="button"
                                    className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded cursor-pointer"
                                    onClick={() => {
                                      const quill = editQuillRef.current?.getEditor();
                                      if (quill) {
                                        const range = quill.getSelection(true);
                                        quill.insertText(range.index, '{last_name}');
                                        quill.setSelection(range.index + 11);
                                      }
                                    }}
                                  >
                                    {'{last_name}'}
                                  </button>
                                  <button
                                    type="button"
                                    className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded cursor-pointer"
                                    onClick={() => {
                                      const quill = editQuillRef.current?.getEditor();
                                      if (quill) {
                                        const range = quill.getSelection(true);
                                        quill.insertText(range.index, '{email}');
                                        quill.setSelection(range.index + 7);
                                      }
                                    }}
                                  >
                                    {'{email}'}
                                  </button>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  className="flex-1"
                                  onClick={() => {
                                    const title = document.getElementById('edit-campaign-title').value;
                                    const subject = document.getElementById('edit-campaign-subject').value;
                                    const listSelect = document.getElementById('edit-campaign-lists');
                                    const selectedLists = Array.from(listSelect.selectedOptions).map(opt => opt.value);
                                    
                                    if (!title || !subject || selectedLists.length === 0) {
                                      toast.error('Please fill in all required fields and select at least one list');
                                      return;
                                    }
                                    
                                    updateEmailCampaign(editingCampaignData.id, { title, subject, list_ids: selectedLists });
                                  }}
                                >
                                  Update Campaign
                                </Button>
                                <Button variant="outline" onClick={() => {
                                  setEditingCampaignData(null);
                                  setEditCampaignContent('');
                                }}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}


                    {/* Info Card */}
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="pt-6">
                        <div className="flex gap-3">
                          <div className="text-2xl">ℹ️</div>
                          <div className="text-sm text-slate-700">
                            <p className="font-semibold mb-2">Quick Tips:</p>
                            <ul className="space-y-1 list-disc list-inside">
                              <li>CSV format: email, first_name, last_name (header required)</li>
                              <li>All links in emails are automatically tracked</li>
                              <li>Email opens are tracked via invisible pixel</li>
                              <li>Use 5+ minutes interval between emails to avoid spam filters</li>
                              <li>Configure SMTP in Settings before sending</li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Airport Contact Finder */}
                {marketingSubTab === 'airports' && (
                  <AirportContactFinder
                    countriesByContinent={countriesByContinent}
                    fetchCountries={fetchCountries}
                    searchAirports={searchAirports}
                    airportSearchType={airportSearchType}
                    setAirportSearchType={setAirportSearchType}
                    selectedContinent={selectedContinent}
                    setSelectedContinent={setSelectedContinent}
                    selectedCountry={selectedCountry}
                    setSelectedCountry={setSelectedCountry}
                    icaoSearch={icaoSearch}
                    setIcaoSearch={setIcaoSearch}
                    airportNameSearch={airportNameSearch}
                    setAirportNameSearch={setAirportNameSearch}
                    airportResults={airportResults}
                    searchingAirports={searchingAirports}
                  />
                )}
              </div>
            </TabsContent>
          )}

          <TabsContent value="experts" data-testid="experts-content">
            <div className="space-y-6">
              {!selectedExpert ? (
                <>
                  {/* Expert List */}
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>AI Experts</CardTitle>
                          <CardDescription>
                            Chat with AI experts trained on Velocity knowledge
                          </CardDescription>
                        </div>
                        {user && isAdmin(user) && (
                          <Button onClick={() => setShowCreateExpert(true)} size="sm">
                            ➕ Create Expert
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {experts.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-slate-600 mb-4">No experts created yet.</p>
                          {user && isAdmin(user) && (
                            <Button onClick={() => setShowCreateExpert(true)}>
                              Create Your First Expert
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {experts.map(expert => (
                            <Card
                              key={expert.id}
                              className="hover:border-blue-500 transition-colors"
                            >
                              <CardContent className="p-6">
                                <div className="text-4xl text-center mb-3">{expert.icon}</div>
                                <h3 className="font-semibold text-center mb-2">{expert.name}</h3>
                                <p className="text-sm text-slate-600 text-center mb-4">{expert.description}</p>
                                <div className="flex gap-2">
                                  <Button
                                    className="flex-1"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedExpert(expert);
                                      setExpertConversation(null);
                                    }}
                                  >
                                    💬 Chat
                                  </Button>
                                  {user && isAdmin(user) && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setEditingExpert(expert)}
                                    >
                                      ✏️
                                    </Button>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>


                  {/* Knowledge Center */}
                  {user && isAdmin(user) && (
                    <div className="space-y-4">
                      <Card>
                        <CardHeader>
                          <CardTitle>📚 Knowledge Center</CardTitle>
                          <CardDescription>
                            Upload documents and text to build the knowledge base for your AI experts
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="doc-title">Title</Label>
                              <input
                                id="doc-title"
                                type="text"
                                className="w-full p-2 border rounded-md mt-1"
                                placeholder="Document title"
                              />
                            </div>
                            <div>
                              <Label htmlFor="doc-category">Category</Label>
                              <input
                                id="doc-category"
                                type="text"
                                className="w-full p-2 border rounded-md mt-1"
                                placeholder="e.g., products, airports, tenders"
                              />
                            </div>
                            <div>
                              <Label htmlFor="doc-tags">Tags (comma-separated)</Label>
                              <input
                                id="doc-tags"
                                type="text"
                                className="w-full p-2 border rounded-md mt-1"
                                placeholder="e.g., AGL, lighting, NextGen"
                              />
                            </div>
                            <div>
                              <Label htmlFor="doc-file">Upload File (PDF/PPT)</Label>
                              <input
                                id="doc-file"
                                type="file"
                                accept=".pdf,.ppt,.pptx"
                                className="block w-full mt-1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="doc-content">Or Paste Text Directly</Label>
                              <textarea
                                id="doc-content"
                                className="w-full h-32 p-3 border rounded-md mt-1"
                                placeholder="Paste document text here..."
                              />
                            </div>
                            <Button
                              onClick={() => {
                                const title = document.getElementById('doc-title').value;
                                const category = document.getElementById('doc-category').value;
                                const tags = document.getElementById('doc-tags').value;
                                const file = document.getElementById('doc-file').files[0];
                                const content = document.getElementById('doc-content').value;
                                
                                if (!title) {
                                  toast.error('Please enter a title');
                                  return;
                                }
                                
                                uploadKnowledgeDoc(file, title, content, category, tags);
                              }}
                              disabled={uploadingKnowledge}
                              className="w-full"
                            >
                              {uploadingKnowledge ? 'Uploading...' : '📤 Upload Knowledge Document'}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle>Knowledge Documents ({knowledgeDocs.length})</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {knowledgeDocs.length === 0 ? (
                            <p className="text-center text-slate-500 py-8">
                              No knowledge documents yet. Upload your first document above.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {knowledgeDocs.map(doc => (
                                <div key={doc.id} className="border rounded p-3 flex justify-between items-start">
                                  <div className="flex-1">
                                    <h4 className="font-semibold">{doc.title}</h4>
                                    <p className="text-sm text-slate-600">
                                      {doc.category && <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs mr-2">{doc.category}</span>}
                                      {doc.file_type && <span className="text-xs text-slate-500">• {doc.file_type.toUpperCase()}</span>}
                                    </p>
                                    {doc.tags && doc.tags.length > 0 && (
                                      <div className="mt-1 flex gap-1 flex-wrap">
                                        {doc.tags.map((tag, i) => (
                                          <span key={i} className="text-xs bg-slate-100 px-2 py-0.5 rounded">{tag}</span>
                                        ))}
                                      </div>
                                    )}
                                    <p className="text-xs text-slate-400 mt-1">
                                      {new Date(doc.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteKnowledgeDoc(doc.id)}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    🗑️
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Create Expert Dialog */}
                  {showCreateExpert && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                      <Card className="max-w-3xl w-full my-8">
                        <CardHeader>
                          <div className="flex justify-between items-center">
                            <CardTitle>Create New Expert</CardTitle>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowCreateExpert(false)}
                            >
                              ✕
                            </Button>
                          </div>
                        </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="expert-name">Expert Name</Label>
                            <input
                              id="expert-name"
                              type="text"
                              className="w-full p-2 border rounded-md mt-1"
                              placeholder="e.g., Product Specialist, Airport Expert"
                            />
                          </div>
                          <div>
                            <Label htmlFor="expert-icon">Icon (emoji)</Label>
                            <input
                              id="expert-icon"
                              type="text"
                              className="w-full p-2 border rounded-md mt-1"
                              placeholder="e.g., 🎯, ✈️, 📊"
                              defaultValue="🤖"
                            />
                          </div>
                          <div>
                            <Label htmlFor="expert-description">Description</Label>
                            <textarea
                              id="expert-description"
                              className="w-full h-20 p-2 border rounded-md mt-1"
                              placeholder="Brief description of what this expert helps with"
                            />
                          </div>
                          <div>
                            <Label htmlFor="expert-prompt">System Prompt</Label>
                            <textarea
                              id="expert-prompt"
                              className="w-full h-32 p-2 border rounded-md mt-1 font-mono text-sm"
                              placeholder="Instructions for the AI expert (e.g., You are a product specialist for Velocity Airport Solutions...)"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              These instructions define how the expert responds. Be specific about tone, expertise, and how to use knowledge documents.
                            </p>
                          </div>
                          
                          {/* Knowledge Document Selection - Same as Edit */}
                          <div>
                            <div className="flex justify-between items-center">
                              <Label>Assigned Knowledge Documents</Label>
                              <div className="flex gap-2 text-xs">
                                <button
                                  type="button"
                                  onClick={() => {
                                    document.querySelectorAll('[data-doc-id]').forEach(cb => cb.checked = true);
                                  }}
                                  className="text-blue-600 hover:underline"
                                >
                                  Select All
                                </button>
                                <span className="text-slate-400">|</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    document.querySelectorAll('[data-doc-id]').forEach(cb => cb.checked = false);
                                  }}
                                  className="text-blue-600 hover:underline"
                                >
                                  Deselect All
                                </button>
                              </div>
                            </div>
                            <input
                              type="text"
                              placeholder="🔍 Filter documents..."
                              className="w-full p-2 border rounded-md mt-1 mb-2 text-sm"
                              value={docFilter}
                              onChange={(e) => setDocFilter(e.target.value)}
                            />
                            <div className="border rounded-md p-3 mt-1 max-h-60 overflow-y-auto space-y-2">
                              {knowledgeDocs.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center py-4">
                                  No knowledge documents available. Upload some in the Knowledge Center first.
                                </p>
                              ) : (
                                knowledgeDocs
                                  .filter(doc => {
                                    if (!docFilter) return true;
                                    const searchLower = docFilter.toLowerCase();
                                    return (
                                      doc.title.toLowerCase().includes(searchLower) ||
                                      (doc.category && doc.category.toLowerCase().includes(searchLower)) ||
                                      (doc.tags && doc.tags.some(tag => tag.toLowerCase().includes(searchLower)))
                                    );
                                  })
                                  .map(doc => (
                                    <label key={doc.id} className="flex items-start gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                                      <input
                                        type="checkbox"
                                        className="mt-1"
                                        data-doc-id={doc.id}
                                      />
                                      <div className="flex-1">
                                        <div className="font-medium text-sm">{doc.title}</div>
                                        {doc.category && (
                                          <span className="text-xs text-slate-500">{doc.category}</span>
                                        )}
                                        {doc.tags && doc.tags.length > 0 && (
                                          <div className="flex gap-1 mt-1">
                                            {doc.tags.slice(0, 3).map(tag => (
                                              <span key={tag} className="text-xs bg-slate-100 px-1 rounded">{tag}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setPreviewingDoc(doc);
                                        }}
                                        className="text-xs"
                                      >
                                        👁️ View
                                      </Button>
                                    </label>
                                  ))
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              Select which knowledge documents this expert should reference when answering questions.
                              {docFilter && ` (${knowledgeDocs.filter(doc => {
                                const searchLower = docFilter.toLowerCase();
                                return doc.title.toLowerCase().includes(searchLower) ||
                                  (doc.category && doc.category.toLowerCase().includes(searchLower)) ||
                                  (doc.tags && doc.tags.some(tag => tag.toLowerCase().includes(searchLower)));
                              }).length} matching)`}
                            </p>
                          </div>

                          <Button
                            onClick={() => {
                              const selectedDocs = Array.from(
                                document.querySelectorAll('[data-doc-id]:checked')
                              ).map(checkbox => checkbox.getAttribute('data-doc-id'));

                              const expertData = {
                                name: document.getElementById('expert-name').value,
                                icon: document.getElementById('expert-icon').value || '🤖',
                                description: document.getElementById('expert-description').value,
                                system_prompt: document.getElementById('expert-prompt').value,
                                knowledge_doc_ids: selectedDocs.join(',')
                              };
                              
                              if (!expertData.name || !expertData.description || !expertData.system_prompt) {
                                toast.error('Please fill in all required fields');
                                return;
                              }
                              
                              createExpert(expertData);
                            }}
                            className="w-full"
                          >
                            Create Expert
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  )}

                  {/* Edit Expert Dialog */}
                  {editingExpert && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                      <Card className="max-w-3xl w-full my-8">
                        <CardHeader>
                          <div className="flex justify-between items-center">
                            <CardTitle>Edit Expert: {editingExpert.name}</CardTitle>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingExpert(null)}
                            >
                              ✕
                            </Button>
                          </div>
                        </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="edit-expert-name">Expert Name</Label>
                            <input
                              id="edit-expert-name"
                              type="text"
                              className="w-full p-2 border rounded-md mt-1"
                              defaultValue={editingExpert.name}
                            />
                          </div>
                          <div>
                            <Label htmlFor="edit-expert-icon">Icon (emoji)</Label>
                            <input
                              id="edit-expert-icon"
                              type="text"
                              className="w-full p-2 border rounded-md mt-1"
                              defaultValue={editingExpert.icon}
                            />
                          </div>
                          <div>
                            <Label htmlFor="edit-expert-description">Description</Label>
                            <textarea
                              id="edit-expert-description"
                              className="w-full h-20 p-2 border rounded-md mt-1"
                              defaultValue={editingExpert.description}
                            />
                          </div>
                          <div>
                            <Label htmlFor="edit-expert-prompt">System Prompt / Training Instructions</Label>
                            <textarea
                              id="edit-expert-prompt"
                              className="w-full h-40 p-2 border rounded-md mt-1 font-mono text-sm"
                              defaultValue={editingExpert.system_prompt}
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              These instructions define how the expert responds. Be specific about tone, expertise, and how to use knowledge documents.
                            </p>
                          </div>
                          
                          {/* Knowledge Document Selection */}
                          <div>
                            <div className="flex justify-between items-center">
                              <Label>Assigned Knowledge Documents</Label>
                              <div className="flex gap-2 text-xs">
                                <button
                                  type="button"
                                  onClick={() => {
                                    document.querySelectorAll('[data-doc-id]').forEach(cb => cb.checked = true);
                                  }}
                                  className="text-blue-600 hover:underline"
                                >
                                  Select All
                                </button>
                                <span className="text-slate-400">|</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    document.querySelectorAll('[data-doc-id]').forEach(cb => cb.checked = false);
                                  }}
                                  className="text-blue-600 hover:underline"
                                >
                                  Deselect All
                                </button>
                              </div>
                            </div>
                            <input
                              type="text"
                              placeholder="🔍 Filter documents..."
                              className="w-full p-2 border rounded-md mt-1 mb-2 text-sm"
                              value={docFilter}
                              onChange={(e) => setDocFilter(e.target.value)}
                            />
                            <div className="border rounded-md p-3 mt-1 max-h-60 overflow-y-auto space-y-2">
                              {knowledgeDocs.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center py-4">
                                  No knowledge documents available. Upload some in the Knowledge Center first.
                                </p>
                              ) : (
                                knowledgeDocs
                                  .filter(doc => {
                                    if (!docFilter) return true;
                                    const searchLower = docFilter.toLowerCase();
                                    return (
                                      doc.title.toLowerCase().includes(searchLower) ||
                                      (doc.category && doc.category.toLowerCase().includes(searchLower)) ||
                                      (doc.tags && doc.tags.some(tag => tag.toLowerCase().includes(searchLower)))
                                    );
                                  })
                                  .map(doc => (
                                    <label key={doc.id} className="flex items-start gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                                      <input
                                        type="checkbox"
                                        className="mt-1"
                                        defaultChecked={editingExpert.knowledge_doc_ids?.includes(doc.id)}
                                        data-doc-id={doc.id}
                                      />
                                      <div className="flex-1">
                                        <div className="font-medium text-sm">{doc.title}</div>
                                        {doc.category && (
                                          <span className="text-xs text-slate-500">{doc.category}</span>
                                        )}
                                        {doc.tags && doc.tags.length > 0 && (
                                          <div className="flex gap-1 mt-1">
                                            {doc.tags.slice(0, 3).map(tag => (
                                              <span key={tag} className="text-xs bg-slate-100 px-1 rounded">{tag}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setPreviewingDoc(doc);
                                        }}
                                        className="text-xs"
                                      >
                                        👁️ View
                                      </Button>
                                    </label>
                                  ))
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              Select which knowledge documents this expert should reference when answering questions.
                              {docFilter && ` (${knowledgeDocs.filter(doc => {
                                const searchLower = docFilter.toLowerCase();
                                return doc.title.toLowerCase().includes(searchLower) ||
                                  (doc.category && doc.category.toLowerCase().includes(searchLower)) ||
                                  (doc.tags && doc.tags.some(tag => tag.toLowerCase().includes(searchLower)));
                              }).length} matching)`}
                            </p>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              onClick={() => {
                                const selectedDocs = Array.from(
                                  document.querySelectorAll('[data-doc-id]:checked')
                                ).map(checkbox => checkbox.getAttribute('data-doc-id'));

                                const expertData = {
                                  name: document.getElementById('edit-expert-name').value,
                                  icon: document.getElementById('edit-expert-icon').value,
                                  description: document.getElementById('edit-expert-description').value,
                                  system_prompt: document.getElementById('edit-expert-prompt').value,
                                  knowledge_doc_ids: selectedDocs.join(','),
                                  is_active: true
                                };
                                
                                if (!expertData.name || !expertData.description || !expertData.system_prompt) {
                                  toast.error('Please fill in all required fields');
                                  return;
                                }
                                
                                updateExpert(editingExpert.id, expertData);
                              }}
                              className="flex-1"
                            >
                              💾 Save Changes
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setEditingExpert(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  )}
                </>
              ) : (
                /* Expert Chat Interface */
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedExpert(null);
                          setExpertConversation(null);
                        }}
                      >
                        ← Back
                      </Button>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{selectedExpert.icon}</span>
                          <CardTitle>{selectedExpert.name}</CardTitle>
                        </div>
                        <CardDescription>{selectedExpert.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Conversation History */}
                    <div 
                      ref={chatContainerRef}
                      className="space-y-4 mb-6 max-h-96 overflow-y-auto scroll-smooth"
                    >
                      {(!expertConversation || expertConversation.messages.length === 0) ? (
                        <div className="text-center py-8 text-slate-500">
                          <p>Start a conversation by typing your question below.</p>
                          <p className="text-sm mt-2">You can also upload files for analysis.</p>
                        </div>
                      ) : (
                        expertConversation.messages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] p-3 rounded-lg ${
                                msg.role === 'user'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-slate-100 text-slate-900'
                              }`}
                            >
                              <div 
                                className="text-sm" 
                                dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                                style={{ whiteSpace: 'pre-wrap' }}
                              />
                              <div
                                className={`text-xs mt-1 ${
                                  msg.role === 'user' ? 'text-blue-100' : 'text-slate-500'
                                }`}
                              >
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="space-y-3">
                      <textarea
                        className="w-full h-24 p-3 border rounded-md"
                        placeholder="Ask your question or describe what you need help with..."
                        value={expertMessage}
                        onChange={(e) => setExpertMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            chatWithExpert();
                          }
                        }}
                      />
                      <div className="flex gap-2">
                        <input
                          type="file"
                          className="flex-1"
                          id="expert-file"
                        />
                        <Button
                          onClick={chatWithExpert}
                          disabled={expertChatting || !expertMessage.trim()}
                          className="px-6"
                        >
                          {expertChatting ? 'Thinking...' : 'Send'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Knowledge Document Preview Modal */}
            {previewingDoc && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-4xl w-full max-h-[90vh] overflow-hidden">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{previewingDoc.title}</CardTitle>
                        <div className="flex gap-2 mt-2">
                          {previewingDoc.category && (
                            <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {previewingDoc.category}
                            </span>
                          )}
                          {previewingDoc.tags && previewingDoc.tags.map(tag => (
                            <span key={tag} className="text-sm bg-slate-100 px-2 py-1 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPreviewingDoc(null)}
                      >
                        ✕
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="overflow-y-auto max-h-[70vh]">
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded">
                        {previewingDoc.content}
                      </pre>
                    </div>
                    <div className="mt-4 text-xs text-slate-500 border-t pt-4">
                      <p>Created: {new Date(previewingDoc.created_at).toLocaleString()}</p>
                      {previewingDoc.file_url && (
                        <p className="mt-1">Original file: {previewingDoc.file_url.split('/').pop()}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="agents" data-testid="agents-content">
            <AgentPortal />
          </TabsContent>

          <TabsContent value="tasks" data-testid="tasks-content">
            <TasksKanban user={user} />
          </TabsContent>

          <TabsContent value="crm" data-testid="crm-content">
            <CRM user={user} />
          </TabsContent>

          <TabsContent value="media" data-testid="media-content">
            <MediaCenter user={user} />
          </TabsContent>

          <TabsContent value="offers" data-testid="offers-content">
            <OfferCenter user={user} />
          </TabsContent>

          <TabsContent value="settings" data-testid="settings-content">
            <SettingsManager />
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </div>
  );
}; // Closes MainApp

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
};

// Route wrapper for login page that redirects if already authenticated
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return user ? <Navigate to="/" replace /> : children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              } 
            />
            <Route 
              path="/*" 
              element={
                <ProtectedRoute>
                  <MainApp />
                </ProtectedRoute>
              } 
            />
          </Routes>
          <Toaster />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;